import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import useSessionTimeout from "@/hooks/useSessionTimeout";
import SessionTimeoutWarning from "@/components/SessionTimeoutWarning";

/* Entrance animation */
const fadeStyle = document.createElement("style");
if (!document.getElementById("hospital-dash-anim")) {
  fadeStyle.id = "hospital-dash-anim";
  fadeStyle.textContent = `@keyframes fadeInUp { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }`;
  document.head.appendChild(fadeStyle);
}

import HospitalLayout from "@/components/layouts/HospitalLayout";
import {
  Building2,
  Wrench,
  Package,
  FileText,
  Bell,
  User,
  LogOut,
  ChevronRight,
  Plus,
  Clock,
  CheckCircle2,
  AlertTriangle,
  Loader2,
  MapPin,
  Phone,
  Calendar,
  Shield,
  Settings,
  HelpCircle,
  Search,
  Filter,
  RefreshCw,
  Eye,
  Send,
  ClipboardList,
  ClipboardCheck,
  Star,
  ThumbsUp,
  ThumbsDown,
  PenTool,
  Mail,
  X,
  Camera,
  Image,
  Trash2,
  Upload,
  Edit,
  History,
  ShoppingCart,
  Lock,
  Key,
  Save,
  BarChart3,
  Users,
  TrendingUp,
  Activity,
  MessageCircle,
  Droplets,
  Layers,
  LayoutList,
  LayoutGrid as LayoutGridIcon,
  Zap,
  Timer,
  DollarSign,
  Award,
  Gavel,
  Trophy,
  RefreshCcw,
  Pencil,
  Briefcase,
  ExternalLink,
  Archive as ArchiveIcon,
  Truck
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import TicketChat from "@/components/TicketChat";
import TelegramConnect from "@/components/TelegramConnect";
import TelegramInviteBanner from "@/components/TelegramInviteBanner";
import EnvironmentalTestingPage from "@/components/hospital/EnvironmentalTestingPage";
import HospitalMaintenancePipeline from "@/components/hospital/HospitalMaintenancePipeline";
import HospitalOrdersPanel from "@/components/hospital/HospitalOrdersPanel";
import HospitalManagedOrdersPanel from "@/components/hospital/HospitalManagedOrdersPanel";
import ImsHartBanner from "@/components/hospital/ImsHartBanner";
import BackorderPipeline from "@/components/BackorderPipeline";
import DashboardHeroBanner, { DashboardSearchBar } from "@/components/ui/DashboardHeroBanner";
import CampaignAlertsPanel from "@/components/CampaignAlertsPanel";
import { QuoteCardList } from "@/components/QuoteCard";
import BackorderDetailView from "@/components/BackorderDetailView";
import PublicSiteHeader from "@/components/PublicSiteHeader";
import NewMaintenanceRequestDialog from "@/components/hospital/NewMaintenanceRequestDialog";
import QuickQuoteFormShared from "@/components/QuickQuoteFormShared";
import {
  HomeHeroV2,
  TrustMarquee,
  MarriageBento,
  CategoryManifesto,
  VsDistributors,
  SolutionsGrid,
  MetricsBand,
  IndustriesWeServe,
  NewsAndInsights,
} from "@/components/HomeHeroV2";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const LOGO_URL = "https://customer-assets.emergentagent.com/job_isaac-platform/artifacts/9qsxumik_Sterile%20Mate%20Logo%20%282%29.jpg";

// Maintenance request types
const REQUEST_TYPES = [
  { id: "water_leak", label: "Water Leak", icon: "💧", priority: "high" },
  { id: "equipment_malfunction", label: "Equipment Malfunction", icon: "⚙️", priority: "high" },
  { id: "hvac_issue", label: "HVAC Issue", icon: "❄️", priority: "normal" },
  { id: "plumbing", label: "Plumbing Issue", icon: "🔧", priority: "normal" },
  { id: "electrical", label: "Electrical Issue", icon: "⚡", priority: "high" },
  { id: "filter_replacement", label: "Filter Replacement", icon: "🔄", priority: "low" },
  { id: "preventive_maintenance", label: "Preventive Maintenance", icon: "📋", priority: "low" },
  { id: "other", label: "Other", icon: "📝", priority: "normal" }
];

export default function HospitalDashboardPortal() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [healthSystem, setHealthSystem] = useState(null);
  const initialTab = searchParams.get("tab") || "home";
  const [activeTab, setActiveTab] = useState(initialTab);
  const [maintenanceViewMode, setMaintenanceViewMode] = useState("table"); // "table" or "pipeline"
  const [backorderViewMode, setBackorderViewMode] = useState("table"); // "table" or "pipeline"
  // Row to highlight after a brand-new backorder submission (read once from ?new=)
  const [highlightedBackorderId, setHighlightedBackorderId] = useState(null);

  // Sync URL with tab changes
  useEffect(() => {
    const tabFromUrl = searchParams.get("tab") || "home";
    if (tabFromUrl !== activeTab) {
      setActiveTab(tabFromUrl);
    }
  }, [searchParams]);

  // When the hospital lands here from a Quick-Quote submission, surface a
  // celebration toast and pin the new row to the top. We consume the param
  // only once so a refresh won't re-fire the toast.
  useEffect(() => {
    const newId = searchParams.get("new");
    if (!newId) return;
    setHighlightedBackorderId(newId);
    toast.success("Your backorder request has been successfully created and added to your dashboard!", { duration: 6000 });
    // Clear the param so toast doesn't repeat on refresh; preserve current tab
    const next = new URLSearchParams(searchParams);
    next.delete("new");
    setSearchParams(next, { replace: true });
    // Remove the highlight after 6 seconds
    const t = setTimeout(() => setHighlightedBackorderId(null), 6000);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    if (newTab === "home") {
      setSearchParams({});
    } else {
      setSearchParams({ tab: newTab });
    }
  };
  
  // Session timeout for security compliance (30 min inactivity)
  const { showWarning, remainingTime, extendSession, formatTime } = useSessionTimeout({
    timeoutMinutes: 30,
    warningMinutes: 5,
    portalType: 'hospital'
  });
  
  // Data states
  const [requests, setRequests] = useState([]);
  const [assets, setAssets] = useState([]);
  const [notifications, setNotifications] = useState([]);
  
  // Modal states
  const [showNewRequest, setShowNewRequest] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showSignOffDialog, setShowSignOffDialog] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [signOffData, setSignOffData] = useState({
    is_approved: true,
    rating: 5,
    feedback: ""
  });
  const [pendingSignOffs, setPendingSignOffs] = useState([]);
  
  // New request form
  const [requestForm, setRequestForm] = useState({
    request_type: "",
    facility_id: "",
    location_details: "",
    description: "",
    priority: "normal",
    asset_id: "",
    additional_emails: [], // Array of additional email recipients
    photos: [] // Array of photo URLs
  });
  const [newEmailInput, setNewEmailInput] = useState("");
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const photoInputRef = React.useRef(null);
  const cameraInputRef = React.useRef(null);
  
  // Edit/Delete request states
  const [showEditRequestModal, setShowEditRequestModal] = useState(false);
  const [editingRequest, setEditingRequest] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingRequest, setDeletingRequest] = useState(null);
  const [showRequestHistoryModal, setShowRequestHistoryModal] = useState(false);
  const [viewingRequestHistory, setViewingRequestHistory] = useState(null);
  
  // Password setup states
  const [showPasswordSetupModal, setShowPasswordSetupModal] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    new_password: "",
    confirm_password: ""
  });
  const [settingPassword, setSettingPassword] = useState(false);
  const [hasPassword, setHasPassword] = useState(false);
  
  // Notification Preferences state
  const [notificationPrefs, setNotificationPrefs] = useState({
    email_new_quickbid: true,
    email_compliance_alert: true,
    email_backorder_sync: true,
    email_workorder_cancel: true,
    email_workorder_new: true,
    email_workorder_complete: true,
    email_workorder_change: true,
    telegram_new_quickbid: true,
    telegram_compliance_alert: true,
    telegram_backorder_sync: true,
    telegram_workorder_cancel: true,
    telegram_workorder_new: true,
    telegram_workorder_complete: true,
    telegram_workorder_change: true
  });
  const [notificationPrefsLoading, setNotificationPrefsLoading] = useState(false);
  const [telegramConnected, setTelegramConnected] = useState(false);
  
  // Compliance Thresholds state
  const [complianceThresholds, setComplianceThresholds] = useState({
    suppliers_threshold: 2500,
    repairs_threshold: 2500,
    equipment_threshold: 5000,
    services_threshold: 2500,
    enabled: true
  });
  const [thresholdsLoading, setThresholdsLoading] = useState(false);
  
  // Backorders state
  const [backorders, setBackorders] = useState([]);
  const [showNewBackorderModal, setShowNewBackorderModal] = useState(false);
  const [showBackorderDetailModal, setShowBackorderDetailModal] = useState(false);
  const [selectedBackorder, setSelectedBackorder] = useState(null);
  const [backorderQuotes, setBackorderQuotes] = useState([]);
  const [loadingQuotes, setLoadingQuotes] = useState(false);
  const [backorderForm, setBackorderForm] = useState({
    item_name: "",
    part_number: "",
    manufacturer: "",
    quantity: 1,
    urgency: "normal",
    notes: "",
    photos: [],
    additional_emails: []
  });
  const [newBackorderEmailInput, setNewBackorderEmailInput] = useState("");
  // Order-History items (Amazon "buy it again" + typeahead). Loaded lazily
  // when the New Backorder modal opens or when the user types in the item-
  // name field.
  const [itemHistory, setItemHistory] = useState([]);
  const [typeaheadResults, setTypeaheadResults] = useState([]);
  const [showTypeahead, setShowTypeahead] = useState(false);

  // Lazy-load order history when the New Backorder modal opens
  useEffect(() => {
    if (!showNewBackorderModal) return;
    const token = localStorage.getItem("hospital_token");
    if (!token) return;
    axios.get(`${API}/hospital/backorders/item-history?limit=12`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => setItemHistory(r.data?.items || []))
      .catch(() => setItemHistory([]));
  }, [showNewBackorderModal]);

  // Typeahead handler — debounced search of the hospital's order history
  useEffect(() => {
    if (!showNewBackorderModal) return;
    const q = backorderForm.item_name?.trim();
    if (!q || q.length < 2) {
      setTypeaheadResults([]);
      setShowTypeahead(false);
      return;
    }
    const token = localStorage.getItem("hospital_token");
    if (!token) return;
    const t = setTimeout(() => {
      axios.get(`${API}/hospital/backorders/item-history?q=${encodeURIComponent(q)}&limit=6`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then((r) => {
          const items = r.data?.items || [];
          setTypeaheadResults(items);
          setShowTypeahead(items.length > 0);
        })
        .catch(() => setTypeaheadResults([]));
    }, 220);
    return () => clearTimeout(t);
  }, [backorderForm.item_name, showNewBackorderModal]);

  const applyHistoryItem = (item) => {
    setBackorderForm((f) => ({
      ...f,
      item_name: item.name || f.item_name,
      part_number: item.part_number || f.part_number,
      manufacturer: item.manufacturer || f.manufacturer,
      quantity: item.last_quantity || f.quantity,
    }));
    setShowTypeahead(false);
  };

  // Archive a backorder (soft hide from live dashboard)
  const archiveBackorder = async (backorderId) => {
    const token = localStorage.getItem("hospital_token");
    try {
      await axios.post(`${API}/hospital/backorders/${backorderId}/archive`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Backorder archived");
      fetchData();
    } catch (e) {
      toast.error("Failed to archive");
    }
  };

  
  // Hospital users state
  const [hospitalUsers, setHospitalUsers] = useState([]);
  
  // Analytics state
  const [analytics, setAnalytics] = useState({
    total_requests: 0,
    completed_requests: 0,
    pending_requests: 0,
    average_resolution_time: 0,
    monthly_requests: []
  });
  
  // QuickBid state
  const [quickbids, setQuickbids] = useState({
    active: [],
    pending_award: [],
    recently_awarded: []
  });
  const [quickbidLoading, setQuickbidLoading] = useState(false);
  const [showNewQuickBidModal, setShowNewQuickBidModal] = useState(false);
  const [showQuickBidDetailModal, setShowQuickBidDetailModal] = useState(false);
  const [showEditQuickBidModal, setShowEditQuickBidModal] = useState(false);
  const [showAwardModal, setShowAwardModal] = useState(false);
  const [selectedQuickBid, setSelectedQuickBid] = useState(null);
  const [quickBidForm, setQuickBidForm] = useState({
    item_name: "",
    description: "",
    estimated_value: "",
    quantity: 1,
    category: "medical_equipment",
    urgency: "normal",
    duration_hours: 24
  });

  // Backorder edit state
  const [showBackorderEditModal, setShowBackorderEditModal] = useState(false);
  const [showAuditTrailModal, setShowAuditTrailModal] = useState(false);
  const [backorderEditForm, setBackorderEditForm] = useState({
    internal_priority: 'normal',
    department_notes: '',
    required_date: '',
    department: '',
    contact_name: '',
    contact_phone: ''
  });
  const [auditTrailData, setAuditTrailData] = useState([]);
  const [auditTrailLoading, setAuditTrailLoading] = useState(false);

  // Contractor list for assignment
  const [availableContractors, setAvailableContractors] = useState([]);
  const [contractorsLoading, setContractorsLoading] = useState(false);
  
  // Hospital-wide audit log state
  const [hospitalAuditLog, setHospitalAuditLog] = useState([]);
  const [auditLogLoading, setAuditLogLoading] = useState(false);

  // DI Tank state
  const [diDeployments, setDiDeployments] = useState([]);
  const [diRequests, setDiRequests] = useState([]);
  const [diFormOpen, setDiFormOpen] = useState(false);
  const [diSubmitting, setDiSubmitting] = useState(false);
  const [diForm, setDiForm] = useState({
    department: "", service_type: "exchange", tank_size: "1.6 cu. ft.",
    tank_type: "Standard DI", requested_date: "", notes: ""
  });

  // Quick issue templates
  const QUICK_ISSUES = [
    { id: "water_leak", label: "Water Leak", icon: "💧", color: "bg-blue-500", priority: "high" },
    { id: "equipment_malfunction", label: "Equipment Down", icon: "⚙️", color: "bg-red-500", priority: "high" },
    { id: "hvac_issue", label: "HVAC Issue", icon: "❄️", color: "bg-cyan-500", priority: "normal" },
    { id: "electrical", label: "Electrical", icon: "⚡", color: "bg-amber-500", priority: "high" },
    { id: "plumbing", label: "Plumbing", icon: "🔧", color: "bg-indigo-500", priority: "normal" },
  ];

  // Open modal with pre-selected issue type
  const openQuickIssue = (issueType) => {
    const issue = QUICK_ISSUES.find(i => i.id === issueType) || REQUEST_TYPES.find(r => r.id === issueType);
    setRequestForm({
      ...requestForm,
      request_type: issueType,
      priority: issue?.priority || "normal"
    });
    setShowNewRequest(true);
  };

  useEffect(() => {
    // Check authentication
    const token = localStorage.getItem("hospital_token");
    const userData = localStorage.getItem("hospital_user");
    const hsData = localStorage.getItem("hospital_health_system");
    
    if (!token || !userData) {
      navigate("/hospital/login");
      return;
    }
    
    const parsedUser = JSON.parse(userData);
    setUser(parsedUser);
    setHealthSystem(JSON.parse(hsData));
    setHasPassword(parsedUser.has_password || false);
    fetchData();
    fetchQuickBids();
  }, [navigate]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem("hospital_token");
      
      // Fetch maintenance requests, assets, pending sign-offs, backorders, and users
      const [requestsRes, assetsRes, signOffsRes, backordersRes, usersRes] = await Promise.all([
        axios.get(`${API}/hospital/my-requests`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: { requests: [] } })),
        axios.get(`${API}/hospital/my-assets`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: { assets: [] } })),
        axios.get(`${API}/hospital/pending-sign-offs`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: { pending_sign_offs: [] } })),
        axios.get(`${API}/hospital/backorders`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: { backorders: [] } })),
        axios.get(`${API}/hospital/my-users`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: { users: [] } }))
      ]);
      
      const reqs = requestsRes.data.requests || [];
      setRequests(reqs);
      setAssets(assetsRes.data.assets || []);
      setPendingSignOffs(signOffsRes.data.pending_sign_offs || []);
      setBackorders(backordersRes.data.backorders || []);
      setHospitalUsers(usersRes.data.users || []);
      
      // Calculate simple analytics from requests
      const completedReqs = reqs.filter(r => r.status === "completed" || r.stage === "closed");
      const pendingReqs = reqs.filter(r => r.status === "pending" || r.stage === "intake");
      setAnalytics({
        total_requests: reqs.length,
        completed_requests: completedReqs.length,
        pending_requests: pendingReqs.length,
        in_progress: reqs.filter(r => r.stage === "in_progress" || r.status === "in_progress").length,
        average_resolution_time: 0
      });
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch DI Tank data
  const fetchDiData = async () => {
    const token = localStorage.getItem("hospital_token");
    if (!token) return;
    try {
      const [depRes, reqRes] = await Promise.all([
        axios.get(`${API}/di-tanks/hospital/deployments`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: [] })),
        axios.get(`${API}/di-tanks/hospital/exchange-requests`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: [] })),
      ]);
      setDiDeployments(Array.isArray(depRes.data) ? depRes.data : []);
      setDiRequests(Array.isArray(reqRes.data) ? reqRes.data : []);
    } catch { /* silent */ }
  };

  useEffect(() => {
    if (activeTab === "di-tanks") fetchDiData();
  }, [activeTab]);

  const handleDiSubmit = async () => {
    if (!diForm.department || !diForm.requested_date) {
      toast.error("Please fill department and requested date");
      return;
    }
    setDiSubmitting(true);
    const token = localStorage.getItem("hospital_token");
    try {
      await axios.post(`${API}/di-tanks/hospital/exchange-requests`, {
        ...diForm,
        company_name: healthSystem?.name || user?.hospital_name || ""
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Exchange request submitted successfully");
      setDiFormOpen(false);
      setDiForm({ department: "", service_type: "exchange", tank_size: "1.6 cu. ft.", tank_type: "Standard DI", requested_date: "", notes: "" });
      fetchDiData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submit failed");
    } finally {
      setDiSubmitting(false);
    }
  };

  // Fetch QuickBid data
  const fetchQuickBids = async () => {
    try {
      setQuickbidLoading(true);
      const token = localStorage.getItem("hospital_token");
      const res = await axios.get(`${API}/hospital/quickbids`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Check for new bids and notify
      const newData = res.data;
      if (quickbids.active && newData.active) {
        for (const newBid of newData.active) {
          const oldBid = quickbids.active.find(b => b.id === newBid.id);
          if (oldBid && newBid.bids_count > (oldBid.bids_count || 0)) {
            const newBidsCount = newBid.bids_count - (oldBid.bids_count || 0);
            toast.success(
              `🎉 New bid received! ${newBid.item_name} now has ${newBid.bids_count} bid${newBid.bids_count > 1 ? 's' : ''}`,
              { duration: 5000 }
            );
          }
        }
      }
      
      setQuickbids(newData);
    } catch (error) {
      console.error("Failed to fetch quickbids:", error);
    } finally {
      setQuickbidLoading(false);
    }
  };

  // Real-time QuickBid polling - check every 30 seconds for new bids
  useEffect(() => {
    if (activeTab === "quickbids" && quickbids.active?.length > 0) {
      const pollInterval = setInterval(() => {
        fetchQuickBids();
      }, 30000); // Poll every 30 seconds
      
      return () => clearInterval(pollInterval);
    }
  }, [activeTab, quickbids.active?.length]);

  // Create new QuickBid
  const handleCreateQuickBid = async () => {
    try {
      const token = localStorage.getItem("hospital_token");
      await axios.post(`${API}/hospital/quickbids`, quickBidForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("QuickBid created successfully! Suppliers will be notified.");
      setShowNewQuickBidModal(false);
      setQuickBidForm({
        item_name: "",
        description: "",
        estimated_value: "",
        quantity: 1,
        category: "medical_equipment",
        urgency: "normal",
        duration_hours: 24
      });
      fetchQuickBids();
    } catch (error) {
      console.error("Failed to create QuickBid:", error);
      toast.error("Failed to create QuickBid");
    }
  };

  // Update QuickBid
  const handleUpdateQuickBid = async () => {
    if (!selectedQuickBid) return;
    try {
      const token = localStorage.getItem("hospital_token");
      await axios.put(`${API}/hospital/quickbids/${selectedQuickBid.id}`, quickBidForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("QuickBid updated successfully");
      setShowEditQuickBidModal(false);
      setSelectedQuickBid(null);
      fetchQuickBids();
    } catch (error) {
      console.error("Failed to update QuickBid:", error);
      toast.error("Failed to update QuickBid");
    }
  };

  // Delete QuickBid
  const handleDeleteQuickBid = async (quickbidId) => {
    if (!confirm("Are you sure you want to cancel this QuickBid?")) return;
    try {
      const token = localStorage.getItem("hospital_token");
      await axios.delete(`${API}/hospital/quickbids/${quickbidId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("QuickBid cancelled");
      fetchQuickBids();
    } catch (error) {
      console.error("Failed to delete QuickBid:", error);
      toast.error("Failed to cancel QuickBid");
    }
  };

  // Award QuickBid to supplier
  const handleAwardQuickBid = async (supplierId, bidAmount) => {
    if (!selectedQuickBid) return;
    try {
      const token = localStorage.getItem("hospital_token");
      await axios.post(`${API}/hospital/quickbids/${selectedQuickBid.id}/award`, {
        supplier_id: supplierId,
        winning_amount: bidAmount
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Supplier awarded successfully! They will be notified.");
      setShowAwardModal(false);
      setSelectedQuickBid(null);
      fetchQuickBids();
    } catch (error) {
      console.error("Failed to award QuickBid:", error);
      toast.error("Failed to award supplier");
    }
  };

  // Update Backorder (hospital editable fields)
  const handleUpdateBackorder = async () => {
    if (!selectedBackorder) return;
    try {
      const token = localStorage.getItem("hospital_token");
      const response = await axios.put(
        `${API}/hospital/backorder-request/${selectedBackorder.id}`,
        backorderEditForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Backorder updated! ${response.data.changes || 0} field(s) changed.`);
      setShowBackorderEditModal(false);
      setSelectedBackorder(null);
      fetchBackorders();
    } catch (error) {
      console.error("Failed to update backorder:", error);
      toast.error("Failed to update backorder");
    }
  };

  // Fetch Audit Trail for a backorder
  const fetchAuditTrail = async (backorderId) => {
    try {
      setAuditTrailLoading(true);
      const token = localStorage.getItem("hospital_token");
      const response = await axios.get(
        `${API}/hospital/backorder-request/${backorderId}/audit-trail`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAuditTrailData(response.data.audit_trail || []);
    } catch (error) {
      console.error("Failed to fetch audit trail:", error);
      toast.error("Failed to load audit trail");
      setAuditTrailData([]);
    } finally {
      setAuditTrailLoading(false);
    }
  };

  // Fetch quotes for a backorder request
  const fetchBackorderQuotes = async (requestId) => {
    try {
      setLoadingQuotes(true);
      const token = localStorage.getItem("hospital_token");
      const response = await axios.get(`${API}/backorders/hospital/requests/${requestId}/quotes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBackorderQuotes(response.data.quotes || []);
    } catch (error) {
      console.error("Error fetching quotes:", error);
      setBackorderQuotes([]);
    } finally {
      setLoadingQuotes(false);
    }
  };

  // Effect to load audit trail when modal opens
  useEffect(() => {
    if (showAuditTrailModal && selectedBackorder) {
      fetchAuditTrail(selectedBackorder.id);
    }
  }, [showAuditTrailModal, selectedBackorder]);

  // Effect to load quotes when backorder detail modal opens
  useEffect(() => {
    if (showBackorderDetailModal && selectedBackorder) {
      fetchBackorderQuotes(selectedBackorder.id);
    } else {
      setBackorderQuotes([]);
    }
  }, [showBackorderDetailModal, selectedBackorder]);

  // Fetch available contractors for assignment
  const fetchContractors = async () => {
    try {
      setContractorsLoading(true);
      const token = localStorage.getItem("hospital_token");
      const response = await axios.get(`${API}/hospital/contractors`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvailableContractors(response.data.contractors || []);
    } catch (error) {
      console.error("Failed to fetch contractors:", error);
    } finally {
      setContractorsLoading(false);
    }
  };

  // Fetch hospital-wide audit log
  const fetchHospitalAuditLog = async (recordType = null) => {
    try {
      setAuditLogLoading(true);
      const token = localStorage.getItem("hospital_token");
      const url = recordType 
        ? `${API}/hospital/audit-log?record_type=${recordType}&limit=100`
        : `${API}/hospital/audit-log?limit=100`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHospitalAuditLog(response.data.audit_entries || []);
    } catch (error) {
      console.error("Failed to fetch audit log:", error);
      toast.error("Failed to load audit log");
    } finally {
      setAuditLogLoading(false);
    }
  };

  // Fetch maintenance request history/audit
  const fetchMaintenanceHistory = async (requestId) => {
    try {
      const token = localStorage.getItem("hospital_token");
      const response = await axios.get(
        `${API}/hospital/maintenance-request/${requestId}/history`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    } catch (error) {
      console.error("Failed to fetch request history:", error);
      return { history: [] };
    }
  };

  // Load settings when Settings tab is active
  useEffect(() => {
    if (activeTab === "settings") {
      fetchHospitalSettings();
    }
  }, [activeTab]);

  const handleLogout = () => {
    localStorage.removeItem("hospital_token");
    localStorage.removeItem("hospital_user");
    localStorage.removeItem("hospital_health_system");
    navigate("/hospital/login");
  };

  // Fetch hospital settings (notification preferences + compliance thresholds)
  const fetchHospitalSettings = async () => {
    try {
      const token = localStorage.getItem("hospital_token");
      const response = await axios.get(`${API}/hospital/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.notification_preferences) {
        setNotificationPrefs(response.data.notification_preferences);
      }
      if (response.data.compliance_thresholds) {
        setComplianceThresholds(response.data.compliance_thresholds);
      }
      setTelegramConnected(response.data.telegram_connected || false);
    } catch (error) {
      console.error("Failed to fetch settings:", error);
    }
  };

  // Save notification preferences
  const saveNotificationPrefs = async () => {
    try {
      setNotificationPrefsLoading(true);
      const token = localStorage.getItem("hospital_token");
      await axios.put(
        `${API}/hospital/settings/notification-preferences`,
        notificationPrefs,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Notification preferences saved!");
    } catch (error) {
      toast.error("Failed to save notification preferences");
    } finally {
      setNotificationPrefsLoading(false);
    }
  };

  // Save compliance thresholds
  const saveComplianceThresholds = async () => {
    try {
      setThresholdsLoading(true);
      const token = localStorage.getItem("hospital_token");
      await axios.put(
        `${API}/hospital/settings/compliance-thresholds`,
        complianceThresholds,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Compliance thresholds saved!");
    } catch (error) {
      toast.error("Failed to save compliance thresholds");
    } finally {
      setThresholdsLoading(false);
    }
  };

  // Toggle notification preference
  const toggleNotificationPref = (key) => {
    setNotificationPrefs(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // Photo upload handler
  const handlePhotoUpload = async (file) => {
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ["image/jpeg", "image/png", "image/webp", "image/heic"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Please upload a valid image (JPEG, PNG, WebP, or HEIC)");
      return;
    }
    
    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image too large. Maximum size is 10MB.");
      return;
    }
    
    setUploadingPhoto(true);
    
    try {
      const formData = new FormData();
      formData.append("file", file);
      
      const response = await axios.post(`${API}/maintenance/upload`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${localStorage.getItem("hospital_token")}`
        }
      });
      
      if (response.data.url) {
        setRequestForm(prev => ({
          ...prev,
          photos: [...prev.photos, response.data.url]
        }));
        toast.success("Photo uploaded successfully");
      }
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Failed to upload photo. Please try again.");
    } finally {
      setUploadingPhoto(false);
    }
  };

  const removePhoto = (index) => {
    setRequestForm(prev => ({
      ...prev,
      photos: prev.photos.filter((_, i) => i !== index)
    }));
  };

  const handleSubmitRequest = async (e) => {
    e.preventDefault();
    
    if (!requestForm.request_type || !requestForm.description) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    setSubmitting(true);
    try {
      const token = localStorage.getItem("hospital_token");
      
      const payload = {
        type: "maintenance",
        request_type: requestForm.request_type,
        facility_id: requestForm.facility_id || user?.facility_id,
        facility_name: healthSystem?.facilities?.find(f => f.id === (requestForm.facility_id || user?.facility_id))?.name,
        health_system_id: healthSystem?.id,
        health_system_name: healthSystem?.name,
        location_details: requestForm.location_details,
        description: requestForm.description,
        priority: requestForm.priority,
        asset_id: requestForm.asset_id || null,
        submitted_by: user?.name,
        submitted_by_email: user?.email,
        contact_phone: user?.phone,
        additional_emails: requestForm.additional_emails || [],
        photos: requestForm.photos || []
      };
      
      await axios.post(`${API}/hospital/maintenance-request`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Maintenance request submitted successfully!");
      setShowNewRequest(false);
      setRequestForm({
        request_type: "",
        facility_id: "",
        location_details: "",
        description: "",
        priority: "normal",
        asset_id: "",
        additional_emails: [],
        photos: []
      });
      setNewEmailInput("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit request");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSignOff = async (approve) => {
    if (!selectedRequest) return;
    
    setSubmitting(true);
    try {
      const token = localStorage.getItem("hospital_token");
      
      await axios.post(
        `${API}/hospital/maintenance-request/${selectedRequest.id}/sign-off`,
        {
          is_approved: approve,
          rating: signOffData.rating,
          feedback: signOffData.feedback
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(approve ? "Work approved successfully!" : "Work rejected. Contractor notified.");
      setShowSignOffDialog(false);
      setSelectedRequest(null);
      setSignOffData({ is_approved: true, rating: 5, feedback: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit sign-off");
    } finally {
      setSubmitting(false);
    }
  };

  const openSignOffDialog = (request) => {
    setSelectedRequest(request);
    setSignOffData({ is_approved: true, rating: 5, feedback: "" });
    setShowSignOffDialog(true);
  };

  // Edit request handler
  const openEditRequestModal = (request) => {
    setEditingRequest({
      ...request,
      description: request.description || "",
      priority: request.priority || "normal",
      location_details: request.location_details || request.location || "",
      assigned_contractor_id: request.assigned_contractor_id || "",
      contact_name: request.contact_name || "",
      contact_phone: request.contact_phone || "",
      notes: request.notes || ""
    });
    // Fetch contractors when opening edit modal
    fetchContractors();
    setShowEditRequestModal(true);
  };

  const handleUpdateRequest = async () => {
    if (!editingRequest) return;
    
    setSubmitting(true);
    try {
      const token = localStorage.getItem("hospital_token");
      const response = await axios.put(
        `${API}/hospital/maintenance-request/${editingRequest.id}`,
        {
          description: editingRequest.description,
          priority: editingRequest.priority,
          location_details: editingRequest.location_details,
          assigned_contractor_id: editingRequest.assigned_contractor_id || null,
          contact_name: editingRequest.contact_name,
          contact_phone: editingRequest.contact_phone,
          notes: editingRequest.notes
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const changes = response.data.changes || 0;
      toast.success(`Request updated successfully! ${changes} field(s) changed.`);
      setShowEditRequestModal(false);
      setEditingRequest(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update request");
    } finally {
      setSubmitting(false);
    }
  };

  // Delete/Cancel request handler
  const openDeleteConfirm = (request) => {
    setDeletingRequest(request);
    setShowDeleteConfirm(true);
  };

  const handleDeleteRequest = async () => {
    if (!deletingRequest) return;
    
    setSubmitting(true);
    try {
      const token = localStorage.getItem("hospital_token");
      await axios.delete(
        `${API}/hospital/maintenance-request/${deletingRequest.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Request deleted successfully! (Archived for audit)");
      setShowDeleteConfirm(false);
      setDeletingRequest(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete request");
    } finally {
      setSubmitting(false);
    }
  };

  // Delete backorder handler
  const handleDeleteBackorder = async (backorderId) => {
    if (!window.confirm("Are you sure you want to delete this backorder? This action will permanently remove it but keep an audit trail.")) {
      return;
    }
    
    try {
      const token = localStorage.getItem("hospital_token");
      await axios.delete(
        `${API}/hospital/backorder-request/${backorderId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Backorder deleted successfully! (Archived for audit)");
      fetchBackorders();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete backorder");
    }
  };

  // View request history
  const openRequestHistory = async (request) => {
    setViewingRequestHistory(request);
    // Fetch the full history from the backend
    const historyData = await fetchMaintenanceHistory(request.id);
    setViewingRequestHistory({ ...request, fullHistory: historyData });
    setShowRequestHistoryModal(true);
  };

  // Password setup handler
  const handleSetPassword = async () => {
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error("Passwords do not match");
      return;
    }
    if (passwordForm.new_password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    
    setSettingPassword(true);
    try {
      const token = localStorage.getItem("hospital_token");
      await axios.post(
        `${API}/hospital/set-password`,
        { new_password: passwordForm.new_password },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Password set successfully! You can now login with your password.");
      setShowPasswordSetupModal(false);
      setPasswordForm({ new_password: "", confirm_password: "" });
      setHasPassword(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to set password");
    } finally {
      setSettingPassword(false);
    }
  };

  // Fetch backorders
  const fetchBackorders = async () => {
    try {
      const token = localStorage.getItem("hospital_token");
      const response = await axios.get(`${API}/hospital/backorders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBackorders(response.data.backorders || []);
    } catch (error) {
      console.error("Failed to fetch backorders:", error);
    }
  };

  // Submit backorder request
  const handleSubmitBackorder = async (e) => {
    e.preventDefault();
    
    if (!backorderForm.item_name) {
      toast.error("Please enter an item name");
      return;
    }
    
    setSubmitting(true);
    try {
      const token = localStorage.getItem("hospital_token");
      // Use the unified `/api/order-request` endpoint so the resulting row is
      // tagged with `hospital_id` and appears in this hospital's Live
      // Backorders dashboard alongside public Quick-Quote submissions.
      const resp = await axios.post(
        `${API}/order-request`,
        {
          order_type: "backorder",
          hospital_id: healthSystem?.id || null,
          hospital_name: healthSystem?.name || backorderForm.hospital_name || "",
          contact_name: user?.name || "",
          contact_email: user?.email || "",
          contact_phone: user?.phone || "",
          items: [{
            name: backorderForm.item_name,
            part_number: backorderForm.part_number,
            manufacturer: backorderForm.manufacturer,
            quantity: parseInt(backorderForm.quantity, 10) || 1,
          }],
          item_name: backorderForm.item_name,
          mfr_number: backorderForm.part_number,
          quantity: parseInt(backorderForm.quantity, 10) || 1,
          notes: backorderForm.notes,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success("Backorder request added to your dashboard");
      const newId = resp.data?.backorder_id || resp.data?.request_id;
      if (newId) setHighlightedBackorderId(newId);
      setShowNewBackorderModal(false);
      setBackorderForm({
        item_name: "",
        part_number: "",
        manufacturer: "",
        quantity: 1,
        urgency: "normal",
        notes: "",
        photos: [],
        additional_emails: []
      });
      setNewBackorderEmailInput("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit backorder request");
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: "bg-amber-100 text-amber-700",
      in_progress: "bg-blue-100 text-blue-700",
      completed: "bg-emerald-100 text-emerald-700",
      dispatched: "bg-purple-100 text-purple-700"
    };
    return styles[status] || "bg-slate-100 text-slate-600";
  };

  const getPriorityBadge = (priority) => {
    const styles = {
      emergency: "bg-red-100 text-red-700",
      high: "bg-orange-100 text-orange-700",
      normal: "bg-blue-100 text-blue-700",
      low: "bg-slate-100 text-slate-600"
    };
    return styles[priority] || styles.normal;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  // Get page title based on active tab
  const getPageTitle = () => {
    switch (activeTab) {
      case "home": return "Welcome";
      case "overview": return "Dashboard Overview";
      case "requests": return "Maintenance Requests";
      case "backorders": return "Backorder Management";
      case "orders": return "My Orders";
      case "assets": return "Equipment & Assets";
      case "sign-offs": return "Work Sign-Offs";
      case "water-testing": return "Environmental Testing";
      case "users": return "User Management";
      case "audit-log": return "Audit Log";
      case "analytics": return "Analytics & Reports";
      case "settings": return "Settings";
      default: return "Hospital Portal";
    }
  };

  // Count live backorders for sidebar badge
  const liveBackordersCount = backorders.filter(b => !['delivered', 'cancelled'].includes(b.status)).length;

  // On the Home tab the public sterilemate.com header takes over and the
  // marketing sections render flush to the viewport edges, so we hide the
  // portal TopHeader and strip the main padding for that tab only.
  const isHomeTab = activeTab === "home";

  return (
    <HospitalLayout
      title={getPageTitle()}
      subtitle={healthSystem?.name || "Hospital Portal"}
      hospital={{ ...healthSystem, contact_name: user?.name, user_name: user?.name }}
      alerts={pendingSignOffs.length}
      liveBackorders={liveBackordersCount}
      onLogout={handleLogout}
      hideTopHeader={isHomeTab}
      mainPadding={isHomeTab ? "p-0" : "p-4 sm:p-6"}
    >
      {/* Session Timeout Warning Modal */}
      <SessionTimeoutWarning
        show={showWarning}
        remainingTime={remainingTime}
        onExtend={extendSession}
        formatTime={formatTime}
      />

      {/* Hero Banner - hidden on Home tab since HomeHeroV2 has its own hero */}
      {activeTab !== "home" && (
        <DashboardHeroBanner 
          portalType="hospital" 
          activeSection={
            activeTab === "requests" ? "maintenance" :
            activeTab === "assets" ? "equipment" :
            activeTab === "sign-offs" ? "signoffs" :
            activeTab === "water-testing" ? "water" :
            activeTab
          }
        />
      )}
      
      {/* Search Bar Below Banner */}
      {activeTab !== "home" && (
        <div className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <DashboardSearchBar 
            placeholder="Search requests, equipment, backorders..."
            className="w-full sm:w-auto"
          />
        </div>
      )}
      
      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList className={`mb-6 flex-wrap gap-1 ${isHomeTab ? "hidden" : ""}`}>
          <TabsTrigger value="home" className="flex items-center gap-2" data-testid="hospital-home-tab">
            <Building2 className="w-4 h-4" />
            <span className="hidden sm:inline">Home</span>
          </TabsTrigger>
          <TabsTrigger value="overview" className="flex items-center gap-2">
            <BarChart3 className="w-4 h-4" />
            <span className="hidden sm:inline">Overview</span>
          </TabsTrigger>
          <TabsTrigger value="requests" className="flex items-center gap-2">
            <Wrench className="w-4 h-4" />
            <span className="hidden sm:inline">Maintenance</span>
          </TabsTrigger>
          <TabsTrigger value="backorders" className="flex items-center gap-2">
            <ShoppingCart className="w-4 h-4" />
            <span className="hidden sm:inline">Backorders</span>
          </TabsTrigger>
          <TabsTrigger value="orders" className="flex items-center gap-2" data-testid="hospital-orders-tab">
            <Package className="w-4 h-4" />
            <span className="hidden sm:inline">My Orders</span>
          </TabsTrigger>
          <TabsTrigger value="quickbids" className="flex items-center gap-2">
            <Gavel className="w-4 h-4" />
            <span className="hidden sm:inline">QuickBids</span>
          </TabsTrigger>
          <TabsTrigger value="assets" className="flex items-center gap-2">
            <Package className="w-4 h-4" />
            <span className="hidden sm:inline">Equipment</span>
          </TabsTrigger>
          <TabsTrigger value="sign-offs" className="relative flex items-center gap-2">
            <ClipboardCheck className="w-4 h-4" />
            <span className="hidden sm:inline">Sign-Offs</span>
            {pendingSignOffs.length > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-amber-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                {pendingSignOffs.length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="water-testing" className="flex items-center gap-2">
            <Droplets className="w-4 h-4" />
            <span className="hidden sm:inline">Microbiology Testing</span>
          </TabsTrigger>
          <TabsTrigger value="di-tanks" className="flex items-center gap-2" data-testid="hospital-di-tanks-tab">
            <Droplets className="w-4 h-4" />
            <span className="hidden sm:inline">DI Tanks</span>
          </TabsTrigger>
          <TabsTrigger value="users" className="flex items-center gap-2">
            <User className="w-4 h-4" />
            <span className="hidden sm:inline">Users</span>
          </TabsTrigger>
          <TabsTrigger value="audit-log" className="flex items-center gap-2">
            <History className="w-4 h-4" />
            <span className="hidden sm:inline">Audit Log</span>
          </TabsTrigger>
          <TabsTrigger value="analytics" className="flex items-center gap-2">
            <BarChart3 className="w-4 h-4" />
            <span className="hidden sm:inline">Analytics</span>
          </TabsTrigger>
          <TabsTrigger value="settings" className="flex items-center gap-2">
            <Settings className="w-4 h-4" />
            <span className="hidden sm:inline">Settings</span>
          </TabsTrigger>
        </TabsList>

        {/* Home Tab — Public sterilemate.com header + homepage embedded inside hospital portal */}
        <TabsContent value="home" className="mt-0">
          <div className="space-y-0" data-testid="hospital-home-content">
            <PublicSiteHeader />
            {/* Personalized welcome banner — slim, sits between public header and hero */}
            <div
              className="bg-gradient-to-r from-[#0a0f1a] via-[#0b1d3a] to-[#0a0f1a] border-b border-amber-400/20"
              data-testid="hospital-welcome-banner"
            >
              <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2.5 flex items-center justify-between gap-4">
                <div className="flex items-center gap-3 min-w-0">
                  <span className="hidden sm:inline-flex w-7 h-7 rounded-full bg-amber-400/15 border border-amber-400/40 items-center justify-center text-amber-300 text-xs font-bold">
                    {(user?.name || "U").charAt(0).toUpperCase()}
                  </span>
                  <p className="text-sm text-slate-200 truncate">
                    Welcome back,{" "}
                    <span className="font-semibold text-white" data-testid="hospital-welcome-name">
                      {user?.name || user?.email || "there"}
                    </span>
                    {healthSystem?.name && (
                      <span className="hidden md:inline text-slate-400">
                        {" · "}
                        {healthSystem.name}
                      </span>
                    )}
                  </p>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <button
                    type="button"
                    onClick={() => handleTabChange("overview")}
                    className="hidden sm:inline-flex text-xs font-semibold text-amber-300 hover:text-amber-200 transition-colors"
                    data-testid="hospital-welcome-dashboard-link"
                  >
                    Go to my dashboard →
                  </button>
                </div>
              </div>
            </div>
            <HomeHeroV2 />
            <TrustMarquee />
            <SolutionsGrid />
            <CategoryManifesto />
            <MarriageBento />
            <VsDistributors />
            <MetricsBand />
            <IndustriesWeServe />
            <NewsAndInsights />
          </div>
        </TabsContent>

        {/* Overview Tab */}
        {/* Overview Tab — IMS HART feature only */}
        <TabsContent value="overview">
          <div className="space-y-6">
            <ImsHartBanner />
          </div>
        </TabsContent>

          {/* Maintenance Requests Tab */}
          <TabsContent value="requests">
            <div className="space-y-4">
              {/* Link to Microbiology Testing */}
              <button
                onClick={() => setActiveTab("water-testing")}
                className="w-full flex items-center justify-between bg-gradient-to-r from-cyan-50 to-teal-50 border border-cyan-200 rounded-xl p-4 hover:from-cyan-100 hover:to-teal-100 transition-colors group"
                data-testid="microbiology-link"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-cyan-500 rounded-lg flex items-center justify-center">
                    <Droplets className="w-5 h-5 text-white" />
                  </div>
                  <div className="text-left">
                    <p className="font-semibold text-cyan-800">Microbiology Testing Services</p>
                    <p className="text-sm text-cyan-600">Schedule water testing, view compliance, download Chain of Custody forms</p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-cyan-400 group-hover:translate-x-1 transition-transform" />
              </button>

              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-slate-900">Maintenance Requests</h2>
                  <p className="text-sm text-slate-500 mt-1">
                    {maintenanceViewMode === "pipeline" ? "Drag tickets between stages to update status" : "View and manage all maintenance requests"}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  {/* View Toggle */}
                  <div className="flex items-center bg-slate-100 rounded-lg p-1">
                    <button
                      onClick={() => setMaintenanceViewMode("table")}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                        maintenanceViewMode === "table" 
                          ? "bg-white text-blue-600 shadow-sm" 
                          : "text-slate-600 hover:text-slate-900"
                      }`}
                      data-testid="view-mode-table"
                    >
                      <LayoutList className="w-4 h-4" />
                      Table
                    </button>
                    <button
                      onClick={() => setMaintenanceViewMode("pipeline")}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                        maintenanceViewMode === "pipeline" 
                          ? "bg-white text-blue-600 shadow-sm" 
                          : "text-slate-600 hover:text-slate-900"
                      }`}
                      data-testid="view-mode-pipeline"
                    >
                      <LayoutGridIcon className="w-4 h-4" />
                      Pipeline
                    </button>
                  </div>
                  <Button onClick={() => setShowNewRequest(true)} className="bg-blue-600 hover:bg-blue-700">
                    <Plus className="w-4 h-4 mr-2" />
                    New Request
                  </Button>
                </div>
              </div>

              {/* Pipeline View */}
              {maintenanceViewMode === "pipeline" ? (
                <HospitalMaintenancePipeline 
                  token={localStorage.getItem("hospital_token")}
                  healthSystem={healthSystem}
                  user={user}
                  onNewRequest={() => setShowNewRequest(true)}
                />
              ) : (
                /* Table View */
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                  <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Type</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Location</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Priority</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {requests.filter(r => r.status !== "cancelled").map((request) => (
                      <tr key={request.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3">
                          <p className="font-medium text-slate-900">
                            {REQUEST_TYPES.find(t => t.id === request.request_type)?.label || request.request_type?.replace("_", " ").replace(/\b\w/g, l => l.toUpperCase())}
                          </p>
                          <p className="text-xs text-slate-500">{request.id}</p>
                          {request.source === "admin_created" && (
                            <span className="text-xs text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">Admin Created</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-slate-600">{request.location_details || request.location || "—"}</td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${getPriorityBadge(request.priority)}`}>
                            {request.priority?.toUpperCase()}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusBadge(request.status || request.stage)}`}>
                            {(request.status || request.stage)?.replace("_", " ").toUpperCase()}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-500">
                          {new Date(request.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            {/* View Details & Chat button - always available */}
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => openRequestHistory(request)}
                              title="View Details & Chat"
                              className="text-purple-600 hover:bg-purple-50"
                            >
                              <MessageCircle className="w-4 h-4" />
                            </Button>
                            
                            {/* Edit button - available for all non-closed requests */}
                            {!["completed", "closed"].includes(request.status) && (
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => openEditRequestModal(request)}
                                title="Edit Request"
                                data-testid={`edit-request-${request.id}`}
                              >
                                <Edit className="w-4 h-4 text-blue-500" />
                              </Button>
                            )}
                            
                            {/* Delete button - available for all non-closed requests */}
                            {!["completed", "closed"].includes(request.status) && (
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => openDeleteConfirm(request)}
                                title="Delete Request"
                                data-testid={`delete-request-${request.id}`}
                              >
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {requests.filter(r => r.status !== "cancelled").length === 0 && (
                  <div className="py-12 text-center">
                    <Wrench className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">No maintenance requests</p>
                    <Button onClick={() => setShowNewRequest(true)} className="mt-4" variant="outline">
                      <Plus className="w-4 h-4 mr-2" />
                      Submit Your First Request
                    </Button>
                  </div>
                )}
              </div>
              )}
            </div>
          </TabsContent>

          {/* Sign-Offs Tab */}
          <TabsContent value="sign-offs">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-slate-900">Work Sign-Offs</h2>
                  <p className="text-sm text-slate-500 mt-1">Review and approve completed maintenance work</p>
                </div>
                <Button variant="outline" onClick={fetchData}>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh
                </Button>
              </div>

              {pendingSignOffs.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-800">
                      {pendingSignOffs.length} work order(s) pending your approval
                    </p>
                    <p className="text-sm text-amber-700 mt-1">
                      Please review and sign off on completed work to close these requests.
                    </p>
                  </div>
                </div>
              )}

              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                {pendingSignOffs.length > 0 ? (
                  <div className="divide-y divide-slate-100">
                    {pendingSignOffs.map((request) => (
                      <div key={request.id} className="p-4 hover:bg-slate-50">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                                <ClipboardCheck className="w-5 h-5 text-amber-600" />
                              </div>
                              <div>
                                <p className="font-medium text-slate-900">
                                  {REQUEST_TYPES.find(t => t.id === request.request_type)?.label || request.request_type || "Maintenance Request"}
                                </p>
                                <p className="text-sm text-slate-500">
                                  {request.location || request.location_details || request.facility_name}
                                </p>
                              </div>
                            </div>
                            <div className="mt-3 pl-13">
                              <p className="text-sm text-slate-600 line-clamp-2">{request.description}</p>
                              <div className="mt-2 flex items-center gap-4 text-xs text-slate-500">
                                <span className="flex items-center gap-1">
                                  <Calendar className="w-3 h-3" />
                                  Submitted: {new Date(request.created_at).toLocaleDateString()}
                                </span>
                                <span className={`px-2 py-0.5 rounded font-medium ${
                                  request.priority === "high" ? "bg-red-100 text-red-700" :
                                  request.priority === "urgent" ? "bg-red-100 text-red-700" :
                                  "bg-slate-100 text-slate-600"
                                }`}>
                                  {request.priority?.toUpperCase()}
                                </span>
                              </div>
                            </div>
                          </div>
                          <Button 
                            onClick={() => openSignOffDialog(request)}
                            className="bg-amber-600 hover:bg-amber-700 text-white"
                            data-testid={`sign-off-btn-${request.id}`}
                          >
                            <PenTool className="w-4 h-4 mr-2" />
                            Sign Off
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="py-12 text-center">
                    <CheckCircle2 className="w-12 h-12 text-emerald-300 mx-auto mb-4" />
                    <p className="text-slate-900 font-medium">All caught up!</p>
                    <p className="text-slate-500 text-sm mt-1">No pending work orders require your sign-off</p>
                  </div>
                )}
              </div>

              {/* Recently Signed Off */}
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-200">
                  <h3 className="font-medium text-slate-900">Recently Approved</h3>
                </div>
                <div className="divide-y divide-slate-100">
                  {requests.filter(r => r.hospital_sign_off).slice(0, 5).map((request) => (
                    <div key={request.id} className="px-4 py-3 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                        <div>
                          <p className="text-sm font-medium text-slate-900">
                            {REQUEST_TYPES.find(t => t.id === request.request_type)?.label || request.request_type}
                          </p>
                          <p className="text-xs text-slate-500">
                            Signed off on {new Date(request.hospital_sign_off?.signed_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      {request.hospital_sign_off?.rating && (
                        <div className="flex items-center gap-1">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <Star 
                              key={star} 
                              className={`w-4 h-4 ${
                                star <= request.hospital_sign_off.rating 
                                  ? "text-amber-400 fill-amber-400" 
                                  : "text-slate-200"
                              }`} 
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                  {requests.filter(r => r.hospital_sign_off).length === 0 && (
                    <div className="px-4 py-6 text-center text-sm text-slate-500">
                      No signed-off work orders yet
                    </div>
                  )}
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Equipment Tab */}
          <TabsContent value="assets">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-slate-900">Equipment & Assets</h2>
                <Button variant="outline" onClick={fetchData}>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh
                </Button>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {assets.map((asset) => (
                  <div key={asset.id} className="bg-white rounded-xl border border-slate-200 p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                        <Package className="w-5 h-5 text-blue-600" />
                      </div>
                      {asset.warranty_status === "under_warranty" && (
                        <span className="px-2 py-1 bg-emerald-100 text-emerald-700 text-xs font-medium rounded flex items-center gap-1">
                          <Shield className="w-3 h-3" />
                          Warranty
                        </span>
                      )}
                    </div>
                    <h3 className="font-medium text-slate-900">{asset.name}</h3>
                    <p className="text-sm text-slate-500 mt-1">{asset.location_details}</p>
                    <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
                      <span>Installed: {new Date(asset.install_date).toLocaleDateString()}</span>
                      {asset.parts_due_count > 0 && (
                        <span className="text-amber-600 font-medium">
                          {asset.parts_due_count} parts due
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {assets.length === 0 && (
                <div className="bg-white rounded-xl border border-slate-200 py-12 text-center">
                  <Package className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No equipment registered for your facilities</p>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Backorders Tab */}
          <TabsContent value="backorders">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-slate-900">Backorder Requests</h2>
                <div className="flex items-center gap-3">
                  {/* View Mode Toggle */}
                  <div className="flex items-center bg-slate-100 rounded-lg p-1">
                    <button
                      onClick={() => setBackorderViewMode("table")}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                        backorderViewMode === "table"
                          ? "bg-white text-slate-900 shadow-sm"
                          : "text-slate-600 hover:text-slate-900"
                      }`}
                      data-testid="backorder-table-view-btn"
                    >
                      <LayoutList className="w-4 h-4" />
                      Table
                    </button>
                    <button
                      onClick={() => setBackorderViewMode("pipeline")}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                        backorderViewMode === "pipeline"
                          ? "bg-white text-slate-900 shadow-sm"
                          : "text-slate-600 hover:text-slate-900"
                      }`}
                      data-testid="backorder-pipeline-view-btn"
                    >
                      <LayoutGridIcon className="w-4 h-4" />
                      Pipeline
                    </button>
                  </div>
                  <Button onClick={() => setShowNewBackorderModal(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="new-backorder-public-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    New Backorder
                  </Button>
                </div>
              </div>

              {/* Pipeline View */}
              {backorderViewMode === "pipeline" ? (
                <BackorderPipeline 
                  viewMode="hospital"
                  hospitalId={healthSystem?.id}
                  authToken={localStorage.getItem("hospital_token")}
                  externalItems={backorders}
                  onStatusChange={(itemId, newStatus) => {
                    // Bi-directional sync: Update local state when Pipeline changes status
                    setBackorders(prev => prev.map(item => 
                      item.id === itemId ? { ...item, status: newStatus } : item
                    ));
                  }}
                  onRefresh={fetchBackorders}
                />
              ) : (
                /* Table View */
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Item</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Part #</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Priority</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Quotes</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Required By</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {[...backorders]
                        .sort((a, b) => {
                          // Pin the newly-created backorder at top until highlight expires
                          if (highlightedBackorderId) {
                            if (a.id === highlightedBackorderId) return -1;
                            if (b.id === highlightedBackorderId) return 1;
                          }
                          return 0;
                        })
                        .map((backorder) => (
                        <tr
                          key={backorder.id}
                          className={`hover:bg-slate-50 transition-colors ${
                            backorder.id === highlightedBackorderId
                              ? "bg-emerald-50 ring-2 ring-emerald-400 animate-pulse"
                              : ""
                          }`}
                          data-testid={`backorder-row-${backorder.id}`}
                        >
                          <td className="px-4 py-3">
                            <p className="font-medium text-slate-900">{backorder.items?.[0]?.name || backorder.item_name || "Item"}</p>
                            <p className="text-xs text-slate-500">{backorder.id}</p>
                            {backorder.department_notes && (
                              <p className="text-xs text-blue-600 mt-1 truncate max-w-[200px]" title={backorder.department_notes}>
                                📝 {backorder.department_notes}
                              </p>
                            )}
                          </td>
                          <td className="px-4 py-3 text-slate-600 text-sm">{backorder.items?.[0]?.part_number || backorder.part_number || "—"}</td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              backorder.internal_priority === 'critical' ? 'bg-red-100 text-red-700' :
                              backorder.internal_priority === 'high' ? 'bg-orange-100 text-orange-700' :
                              backorder.internal_priority === 'medium' ? 'bg-amber-100 text-amber-700' :
                              'bg-slate-100 text-slate-700'
                            }`}>
                              {(backorder.internal_priority || 'normal').toUpperCase()}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusBadge(backorder.status)}`}>
                              {backorder.status?.replace("_", " ").toUpperCase() || "PENDING"}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            {(backorder.status === "quotes_received" || backorder.status === "sent_to_suppliers") ? (
                              <button
                                onClick={() => {
                                  setSelectedBackorder(backorder);
                                  setShowBackorderDetailModal(true);
                                }}
                                className="flex items-center gap-1 px-2 py-1 rounded bg-green-100 text-green-700 text-xs font-medium hover:bg-green-200 transition-colors"
                              >
                                <DollarSign className="w-3 h-3" />
                                View Quotes
                              </button>
                            ) : (
                              <span className="text-xs text-slate-400">—</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-500">
                            {backorder.required_date ? new Date(backorder.required_date).toLocaleDateString() : "—"}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1">
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => {
                                  setSelectedBackorder(backorder);
                                  setShowBackorderDetailModal(true);
                                }}
                                title="View Details & Quotes"
                                className="text-purple-600 hover:bg-purple-50"
                              >
                                <MessageCircle className="w-4 h-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => {
                                  setSelectedBackorder(backorder);
                                  setBackorderEditForm({
                                    internal_priority: backorder.internal_priority || 'normal',
                                    department_notes: backorder.department_notes || '',
                                    required_date: backorder.required_date || '',
                                    department: backorder.department || '',
                                    contact_name: backorder.contact_name || '',
                                    contact_phone: backorder.contact_phone || ''
                                  });
                                  setShowBackorderEditModal(true);
                                }}
                                title="Edit Backorder"
                                className="text-blue-600 hover:bg-blue-50"
                                data-testid={`edit-backorder-${backorder.id}`}
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => {
                                  setSelectedBackorder(backorder);
                                  setShowAuditTrailModal(true);
                                }}
                                title="View Audit Trail"
                                className="text-slate-600 hover:bg-slate-50"
                              >
                                <History className="w-4 h-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => {
                                  if (window.confirm("Archive this backorder? It will be hidden from your live dashboard but the history is preserved.")) {
                                    archiveBackorder(backorder.id);
                                  }
                                }}
                                title="Archive (hide from live dashboard)"
                                className="text-amber-600 hover:bg-amber-50"
                                data-testid={`archive-backorder-${backorder.id}`}
                              >
                                <ArchiveIcon className="w-4 h-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => handleDeleteBackorder(backorder.id)}
                                title="Delete Backorder"
                                className="text-red-600 hover:bg-red-50"
                                data-testid={`delete-backorder-${backorder.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {backorders.length === 0 && (
                    <div className="py-12 text-center">
                      <ShoppingCart className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                      <p className="text-slate-500">No backorder requests</p>
                      <p className="text-xs text-slate-400 mt-1">Request quotes for backordered items from our supplier network</p>
                      <Button onClick={() => setShowNewBackorderModal(true)} className="mt-4" variant="outline" data-testid="create-backorder-public-btn">
                        <Plus className="w-4 h-4 mr-2" />
                        Create Backorder Request
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </TabsContent>

          {/* My Orders Tab */}
          <TabsContent value="orders">
            <div className="space-y-6">
              <HospitalOrdersPanel />
              <HospitalManagedOrdersPanel />
            </div>
          </TabsContent>

          {/* QuickBids Tab */}
          <TabsContent value="quickbids">
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-slate-900">QuickBid Center</h2>
                  <p className="text-sm text-slate-500 mt-1">
                    Competitive bidding for high-value requests
                    {complianceThresholds.enabled && (
                      <span className="ml-1 text-emerald-600">
                        (Thresholds: Suppliers ≥${complianceThresholds.suppliers_threshold?.toLocaleString()}, 
                        Repairs ≥${complianceThresholds.repairs_threshold?.toLocaleString()})
                      </span>
                    )}
                  </p>
                </div>
                <Button 
                  onClick={() => setShowNewQuickBidModal(true)} 
                  className="bg-amber-600 hover:bg-amber-700"
                  data-testid="create-quickbid-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Start New QuickBid
                </Button>
              </div>

              {/* QuickBid Overview Cards */}
              <div className="grid md:grid-cols-3 gap-4">
                <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-xl border border-amber-200 p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-amber-500 rounded-lg flex items-center justify-center">
                      <Gavel className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-amber-900">{quickbids.active?.length || 0}</p>
                      <p className="text-sm text-amber-700">Active Bids</p>
                    </div>
                  </div>
                  <p className="text-xs text-amber-600">Flash bidding sessions in progress</p>
                </div>
                
                <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-xl border border-emerald-200 p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-emerald-500 rounded-lg flex items-center justify-center">
                      <Trophy className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-emerald-900">{quickbids.pending_award?.length || 0}</p>
                      <p className="text-sm text-emerald-700">Pending Award</p>
                    </div>
                  </div>
                  <p className="text-xs text-emerald-600">Awaiting your decision</p>
                </div>
                
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl border border-blue-200 p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                      <CheckCircle2 className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-blue-900">{quickbids.recently_awarded?.length || 0}</p>
                      <p className="text-sm text-blue-700">Recently Awarded</p>
                    </div>
                  </div>
                  <p className="text-xs text-blue-600">Last 30 days</p>
                </div>
              </div>

              {/* Active QuickBids - Enhanced with Edit/Delete */}
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="px-5 py-4 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
                  <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                    <Timer className="w-5 h-5 text-amber-500" />
                    Active Flash Bids
                  </h3>
                  <Button variant="outline" size="sm" onClick={fetchQuickBids} data-testid="refresh-quickbids-btn">
                    <RefreshCcw className="w-4 h-4 mr-1" />
                    Refresh
                  </Button>
                </div>
                
                {quickbids.active && quickbids.active.length > 0 ? (
                  <div className="divide-y divide-slate-100">
                    {quickbids.active.map((bid) => (
                      <div key={bid.id} className="p-5 hover:bg-slate-50 transition-colors">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <h4 className="font-medium text-slate-900">{bid.item_name || bid.description}</h4>
                              <span className="text-xs px-2 py-1 bg-amber-100 text-amber-700 rounded-full animate-pulse">
                                LIVE
                              </span>
                            </div>
                            <p className="text-sm text-slate-500 mt-1">
                              {bid.bids_count || 0} bids received • 
                              <span className="text-amber-600 font-medium ml-1">
                                {bid.time_remaining || "60"} min remaining
                              </span>
                            </p>
                            <p className="text-xs text-slate-400 mt-1">ID: {bid.id}</p>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="text-right">
                              <p className="text-lg font-bold text-slate-900">
                                ${bid.estimated_value?.toLocaleString() || "—"}
                              </p>
                              <p className="text-xs text-slate-500">Estimated value</p>
                            </div>
                            <div className="flex items-center gap-1">
                              <Button 
                                variant="outline" 
                                size="sm" 
                                onClick={() => {
                                  setSelectedQuickBid(bid);
                                  setShowQuickBidDetailModal(true);
                                }}
                                data-testid={`view-quickbid-${bid.id}`}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button 
                                variant="outline" 
                                size="sm" 
                                onClick={() => {
                                  setSelectedQuickBid(bid);
                                  setShowEditQuickBidModal(true);
                                }}
                                data-testid={`edit-quickbid-${bid.id}`}
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button 
                                variant="outline" 
                                size="sm" 
                                className="text-red-600 hover:bg-red-50"
                                onClick={() => handleDeleteQuickBid(bid.id)}
                                data-testid={`delete-quickbid-${bid.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="py-12 text-center">
                    <Gavel className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">No active QuickBid sessions</p>
                    <p className="text-xs text-slate-400 mt-1 mb-4">
                      Start a QuickBid to get competitive quotes from suppliers
                    </p>
                    <Button 
                      onClick={() => setShowNewQuickBidModal(true)}
                      className="bg-amber-600 hover:bg-amber-700"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Start Your First QuickBid
                    </Button>
                  </div>
                )}
              </div>

              {/* Pending Award QuickBids - Enhanced with Award functionality */}
              {quickbids.pending_award && quickbids.pending_award.length > 0 && (
                <div className="bg-white rounded-xl border border-emerald-200 overflow-hidden">
                  <div className="px-5 py-4 border-b border-emerald-200 bg-emerald-50">
                    <h3 className="font-semibold text-emerald-900 flex items-center gap-2">
                      <Award className="w-5 h-5 text-emerald-600" />
                      Ready to Award ({quickbids.pending_award.length})
                    </h3>
                  </div>
                  <div className="divide-y divide-slate-100">
                    {quickbids.pending_award.map((bid) => (
                      <div key={bid.id} className="p-5">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h4 className="font-medium text-slate-900">{bid.item_name || bid.description}</h4>
                            <p className="text-sm text-slate-500 mt-1">
                              {bid.bids_count || 0} bids received
                            </p>
                            {bid.lowest_bid && (
                              <div className="mt-2 flex items-center gap-3">
                                <span className="text-lg font-bold text-emerald-600">
                                  Best: ${bid.lowest_bid?.toLocaleString()}
                                </span>
                                {bid.savings_percent && (
                                  <span className="text-xs px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full">
                                    {bid.savings_percent}% savings
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <Button 
                              variant="outline"
                              onClick={() => {
                                setSelectedQuickBid(bid);
                                setShowQuickBidDetailModal(true);
                              }}
                            >
                              View Bids
                            </Button>
                            <Button 
                              className="bg-emerald-600 hover:bg-emerald-700"
                              onClick={() => {
                                setSelectedQuickBid(bid);
                                setShowAwardModal(true);
                              }}
                              data-testid={`award-quickbid-${bid.id}`}
                            >
                              <Trophy className="w-4 h-4 mr-2" />
                              Award Supplier
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Recently Awarded */}
              {quickbids.recently_awarded && quickbids.recently_awarded.length > 0 && (
                <div className="bg-white rounded-xl border border-blue-200 overflow-hidden">
                  <div className="px-5 py-4 border-b border-blue-200 bg-blue-50">
                    <h3 className="font-semibold text-blue-900 flex items-center gap-2">
                      <CheckCircle2 className="w-5 h-5 text-blue-600" />
                      Recently Awarded ({quickbids.recently_awarded.length})
                    </h3>
                  </div>
                  <div className="divide-y divide-slate-100">
                    {quickbids.recently_awarded.map((bid) => (
                      <div key={bid.id} className="p-5">
                        <div className="flex items-start justify-between">
                          <div>
                            <h4 className="font-medium text-slate-900">{bid.item_name || bid.description}</h4>
                            <p className="text-sm text-slate-500 mt-1">
                              Awarded to: <span className="font-medium">{bid.winner_name || "Supplier"}</span>
                            </p>
                            <p className="text-xs text-slate-400 mt-1">
                              {new Date(bid.awarded_at || bid.updated_at).toLocaleDateString()}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-lg font-bold text-blue-600">
                              ${bid.winning_amount?.toLocaleString() || bid.lowest_bid?.toLocaleString() || "—"}
                            </p>
                            <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded-full">
                              COMPLETED
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Info Section */}
              <div className="bg-slate-50 rounded-xl border border-slate-200 p-5">
                <h4 className="font-medium text-slate-900 mb-2 flex items-center gap-2">
                  <Shield className="w-4 h-4 text-blue-600" />
                  Procurement Compliance
                </h4>
                <p className="text-sm text-slate-600">
                  QuickBid automatically triggers competitive bidding when requests exceed your configured thresholds, 
                  ensuring compliance with procurement requirements. Configure thresholds per category in{" "}
                  <button 
                    onClick={() => setActiveTab("settings")} 
                    className="text-blue-600 hover:underline font-medium"
                  >
                    Settings → Compliance Thresholds
                  </button>. 
                  All bid documentation is preserved for audit purposes.
                </p>
              </div>
            </div>
          </TabsContent>

          {/* Water Testing Tab */}
          <TabsContent value="water-testing">
            <div className="space-y-6">
              {/* Microbiology Testing Banner */}
              <div className="bg-gradient-to-r from-cyan-600 to-teal-600 rounded-xl p-5 text-white shadow-lg">
                <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center">
                      <Droplets className="w-8 h-8 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold">Microbiology Testing Services</h3>
                      <p className="text-cyan-100 text-sm mt-1">ISO 17025 Accredited • AAMI ST108 Compliant • AB Labs Partnership</p>
                    </div>
                  </div>
                  <a
                    href="https://ablabs.com/environmental-coc-071708.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-5 py-3 bg-white text-cyan-700 rounded-lg hover:bg-cyan-50 transition-colors font-medium shadow-md"
                    data-testid="chain-of-custody-link"
                  >
                    <FileText className="w-5 h-5" />
                    Chain of Custody Form
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </div>
              </div>

              {/* Environmental Testing Component */}
              <EnvironmentalTestingPage 
                healthSystemId={healthSystem?.id}
                userInfo={{ name: healthSystem?.admin_name, email: healthSystem?.admin_email }}
              />
            </div>
          </TabsContent>

          {/* DI Tanks Tab */}
          <TabsContent value="di-tanks">
            <div className="space-y-6" data-testid="hospital-di-section">
              {/* Header */}
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
                    <Droplets className="w-5 h-5 text-cyan-600" /> DI Water Tank Exchange Program
                  </h2>
                  <p className="text-sm text-slate-500 mt-1">View your deployed DI tanks and request exchanges</p>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={fetchDiData}>
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                  <Button onClick={() => setDiFormOpen(!diFormOpen)} className="bg-cyan-700 hover:bg-cyan-800 text-white" data-testid="request-exchange-btn">
                    <Plus className="w-4 h-4 mr-2" /> Request Exchange
                  </Button>
                </div>
              </div>

              {/* Exchange Request Form */}
              {diFormOpen && (
                <div className="bg-white border border-cyan-200 rounded-xl p-6 space-y-4 shadow-sm" data-testid="di-exchange-form">
                  <h4 className="font-semibold text-slate-900 flex items-center gap-2">
                    <Send className="w-4 h-4 text-cyan-600" /> Request DI Tank Exchange
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Company / Organization</Label>
                      <Input value={healthSystem?.name || user?.hospital_name || ""} disabled className="bg-slate-50" />
                    </div>
                    <div>
                      <Label>Site Department *</Label>
                      <Input
                        value={diForm.department}
                        onChange={e => setDiForm(f => ({ ...f, department: e.target.value }))}
                        placeholder="e.g. SPD, Lab, Dialysis, Pharmacy"
                        data-testid="di-form-department"
                      />
                    </div>
                    <div>
                      <Label>Service Type</Label>
                      <Select value={diForm.service_type} onValueChange={v => setDiForm(f => ({ ...f, service_type: v }))}>
                        <SelectTrigger data-testid="di-form-service-type"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="new">New Installation</SelectItem>
                          <SelectItem value="exchange">Tank Exchange</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Tank Type</Label>
                      <Select value={diForm.tank_type} onValueChange={v => setDiForm(f => ({ ...f, tank_type: v }))}>
                        <SelectTrigger data-testid="di-form-tank-type"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {["Standard DI", "Mixed Bed DI", "Cation", "Anion", "Carbon"].map(t => (
                            <SelectItem key={t} value={t}>{t}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Tank Size</Label>
                      <Select value={diForm.tank_size} onValueChange={v => setDiForm(f => ({ ...f, tank_size: v }))}>
                        <SelectTrigger data-testid="di-form-tank-size"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {["0.5 cu. ft.", "1.0 cu. ft.", "1.6 cu. ft.", "2.0 cu. ft.", "3.6 cu. ft."].map(s => (
                            <SelectItem key={s} value={s}>{s}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Requested Date *</Label>
                      <Input
                        type="date"
                        value={diForm.requested_date}
                        onChange={e => setDiForm(f => ({ ...f, requested_date: e.target.value }))}
                        data-testid="di-form-date"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Notes (optional)</Label>
                    <Input
                      value={diForm.notes}
                      onChange={e => setDiForm(f => ({ ...f, notes: e.target.value }))}
                      placeholder="Additional details or special requirements..."
                    />
                  </div>
                  <div className="flex gap-2 justify-end">
                    <Button variant="outline" onClick={() => setDiFormOpen(false)}>Cancel</Button>
                    <Button onClick={handleDiSubmit} disabled={diSubmitting} className="bg-cyan-700 hover:bg-cyan-800 text-white" data-testid="di-form-submit">
                      {diSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                      Submit Request
                    </Button>
                  </div>
                </div>
              )}

              {/* Current DI Deployments */}
              <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                <div className="p-4 border-b border-slate-100">
                  <h4 className="text-slate-900 font-medium">Your Active DI Tanks</h4>
                </div>
                {diDeployments.length === 0 ? (
                  <div className="p-12 text-center">
                    <Droplets className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">No DI tanks currently deployed at your facility.</p>
                  </div>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {diDeployments.map(dep => (
                      <div key={dep.id} className={`p-4 ${dep.exchange_overdue ? "bg-red-50" : dep.exchange_urgent ? "bg-amber-50" : ""}`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${dep.exchange_overdue ? "bg-red-100" : dep.exchange_urgent ? "bg-amber-100" : "bg-cyan-100"}`}>
                              <Droplets className={`w-5 h-5 ${dep.exchange_overdue ? "text-red-600" : dep.exchange_urgent ? "text-amber-600" : "text-cyan-600"}`} />
                            </div>
                            <div>
                              {dep.location && <p className="text-xs text-cyan-600 font-medium">{dep.location}</p>}
                              <p className="font-medium text-slate-900">{dep.department}</p>
                              <p className="text-sm text-slate-500">{dep.tank_type} - {dep.tank_size}</p>
                              {dep.contractor_supplier && (
                                <p className="text-xs text-slate-400 mt-0.5">Contractor: {dep.contractor_supplier}{dep.technician_name ? ` / Tech: ${dep.technician_name}` : ""}</p>
                              )}
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-sm text-slate-500">Next Exchange</p>
                            <p className={`font-medium ${dep.exchange_overdue ? "text-red-600" : dep.exchange_urgent ? "text-amber-600" : "text-slate-900"}`}>
                              {dep.exchange_date}
                            </p>
                            {dep.exchange_overdue && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700 mt-1">Overdue {Math.abs(dep.days_until_exchange)}d</span>
                            )}
                            {!dep.exchange_overdue && dep.exchange_urgent && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700 mt-1">{dep.days_until_exchange} days left</span>
                            )}
                            {!dep.exchange_overdue && !dep.exchange_urgent && dep.days_until_exchange != null && (
                              <span className="text-xs text-slate-400">{dep.days_until_exchange} days</span>
                            )}
                          </div>
                        </div>
                        {/* Attachments inline */}
                        <div className="mt-3 ml-14">
                          {dep.attachments?.length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-2">
                              {dep.attachments.map(att => (
                                <a key={att.id} href={`${API}/di-tanks/attachments/${att.stored_name}`} target="_blank" rel="noreferrer"
                                  className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white border border-slate-200 rounded-md text-xs text-slate-600 hover:border-cyan-300 hover:text-cyan-700 transition-colors">
                                  <FileText className="w-3 h-3" />
                                  <span className="truncate max-w-[150px]">{att.original_name}</span>
                                  <span className={`px-1 py-0 rounded text-[9px] font-medium ${att.uploaded_by_role === "admin" ? "bg-blue-100 text-blue-600" : att.uploaded_by_role === "contractor" ? "bg-orange-100 text-orange-600" : "bg-green-100 text-green-600"}`}>{att.uploaded_by_role}</span>
                                </a>
                              ))}
                            </div>
                          )}
                          <label className="inline-flex items-center gap-1.5 px-2.5 py-1 border border-dashed border-slate-300 rounded-md text-xs text-slate-400 hover:text-cyan-600 hover:border-cyan-300 cursor-pointer transition-colors" data-testid={`hospital-upload-${dep.id}`}>
                            <Upload className="w-3 h-3" />
                            <span>Upload Work Order</span>
                            <input type="file" className="hidden" accept=".pdf,.doc,.docx,.xlsx,.xls,.png,.jpg,.jpeg,.csv,.txt"
                              onChange={async (e) => {
                                const file = e.target.files?.[0];
                                if (!file) return;
                                const fd = new FormData();
                                fd.append("file", file);
                                const tkn = localStorage.getItem("hospital_token");
                                try {
                                  const res = await axios.post(`${API}/di-tanks/deployments/${dep.id}/attachments/hospital`, fd, {
                                    headers: { Authorization: `Bearer ${tkn}` }
                                  });
                                  if (res.data?.id) { toast.success(`Uploaded: ${file.name}`); fetchDiData(); }
                                } catch { toast.error("Upload failed"); }
                                e.target.value = "";
                              }} />
                          </label>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Exchange Requests */}
              <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                <div className="p-4 border-b border-slate-100">
                  <h4 className="text-slate-900 font-medium">Your Exchange Requests</h4>
                </div>
                {diRequests.length === 0 ? (
                  <div className="p-12 text-center">
                    <Calendar className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">No exchange requests submitted yet.</p>
                  </div>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {diRequests.map(req => (
                      <div key={req.id} className="p-4 flex items-center justify-between">
                        <div>
                          <p className="font-medium text-slate-900">{req.department}</p>
                          <p className="text-sm text-slate-500">{req.tank_type} - {req.tank_size} | {req.service_type === "new" ? "New Install" : "Exchange"}</p>
                          <p className="text-xs text-slate-400 mt-1">Requested: {req.requested_date}</p>
                        </div>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          req.status === "pending" ? "bg-amber-100 text-amber-700" :
                          req.status === "scheduled" ? "bg-blue-100 text-blue-700" :
                          req.status === "completed" ? "bg-green-100 text-green-700" :
                          "bg-slate-100 text-slate-700"
                        }`}>
                          {req.status?.charAt(0).toUpperCase() + req.status?.slice(1)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Audit Log Tab */}
          <TabsContent value="audit-log">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-slate-900">Audit Log</h2>
                  <p className="text-sm text-slate-500 mt-1">
                    Track all changes to work orders and backorders
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Select 
                    defaultValue="all" 
                    onValueChange={(value) => fetchHospitalAuditLog(value === "all" ? null : value)}
                  >
                    <SelectTrigger className="w-40">
                      <SelectValue placeholder="Filter by type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Records</SelectItem>
                      <SelectItem value="maintenance">Maintenance</SelectItem>
                      <SelectItem value="backorder">Backorders</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button 
                    variant="outline" 
                    onClick={() => fetchHospitalAuditLog()}
                    disabled={auditLogLoading}
                  >
                    <RefreshCw className={`w-4 h-4 mr-2 ${auditLogLoading ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                </div>
              </div>

              {/* Audit Log Info Banner */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <History className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-blue-800">Comprehensive Activity Tracking</p>
                    <p className="text-sm text-blue-700 mt-1">
                      This log shows all changes made to your work orders and backorders, 
                      including updates from hospital staff, admin, and contractors.
                    </p>
                  </div>
                </div>
              </div>

              {/* Load on first view */}
              {hospitalAuditLog.length === 0 && !auditLogLoading && (
                <div className="text-center py-12 bg-white rounded-xl border border-slate-200">
                  <History className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500 mb-4">Click refresh to load the audit log</p>
                  <Button onClick={() => fetchHospitalAuditLog()}>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Load Audit Log
                  </Button>
                </div>
              )}

              {auditLogLoading && (
                <div className="text-center py-12 bg-white rounded-xl border border-slate-200">
                  <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-4" />
                  <p className="text-slate-500">Loading audit log...</p>
                </div>
              )}

              {/* Audit Entries List */}
              {hospitalAuditLog.length > 0 && !auditLogLoading && (
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                  <div className="divide-y divide-slate-100 max-h-[600px] overflow-y-auto">
                    {hospitalAuditLog.map((entry, index) => (
                      <div key={entry.id || index} className="p-4 hover:bg-slate-50">
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-3">
                            {/* Action Icon */}
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                              entry.action === 'delete' ? 'bg-red-100 text-red-600' :
                              entry.action === 'create' || entry.action === 'created' ? 'bg-emerald-100 text-emerald-600' :
                              'bg-blue-100 text-blue-600'
                            }`}>
                              {entry.action === 'delete' ? <Trash2 className="w-4 h-4" /> :
                               entry.action === 'create' || entry.action === 'created' ? <Plus className="w-4 h-4" /> :
                               <Pencil className="w-4 h-4" />}
                            </div>
                            
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-slate-900 capitalize">
                                  {entry.action || 'Update'}
                                </span>
                                <span className={`px-2 py-0.5 text-xs font-medium rounded ${
                                  entry.collection === 'maintenance_requests' 
                                    ? 'bg-purple-100 text-purple-700' 
                                    : 'bg-amber-100 text-amber-700'
                                }`}>
                                  {entry.collection === 'maintenance_requests' ? 'Work Order' : 'Backorder'}
                                </span>
                                {entry.performed_by_type && (
                                  <span className={`px-2 py-0.5 text-xs rounded ${
                                    entry.performed_by_type === 'hospital' ? 'bg-blue-50 text-blue-600' :
                                    entry.performed_by_type === 'admin' ? 'bg-red-50 text-red-600' :
                                    entry.performed_by_type === 'contractor' ? 'bg-green-50 text-green-600' :
                                    'bg-slate-50 text-slate-600'
                                  }`}>
                                    {entry.performed_by_type}
                                  </span>
                                )}
                              </div>
                              
                              <p className="text-sm text-slate-600 mt-1">
                                #{entry.record_id?.substring(0, 8) || 'N/A'}
                                {entry.additional_data?.request_type && 
                                  ` • ${entry.additional_data.request_type.replace('_', ' ')}`}
                              </p>
                              
                              {/* Changes Summary */}
                              {entry.changes && (
                                <p className="text-sm text-slate-500 mt-1">
                                  {typeof entry.changes === 'string' ? entry.changes : 
                                   Array.isArray(entry.changes) ? entry.changes.join(', ') :
                                   entry.changes.updated_fields?.join(', ') || 'Details updated'}
                                </p>
                              )}
                              
                              {/* Changed Values */}
                              {Object.keys(entry.old_values || {}).length > 0 && (
                                <div className="mt-2 text-xs space-y-1">
                                  {Object.entries(entry.old_values).slice(0, 3).map(([key, val]) => (
                                    <div key={key} className="flex items-center gap-2">
                                      <span className="text-slate-400">{key.replace('_', ' ')}:</span>
                                      <span className="text-red-500 line-through">{String(val).substring(0, 30)}</span>
                                      <span className="text-slate-400">→</span>
                                      <span className="text-emerald-600">
                                        {String(entry.new_values?.[key] || '').substring(0, 30)}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                          
                          <div className="text-right">
                            <p className="text-xs text-slate-500">
                              {entry.timestamp ? new Date(entry.timestamp).toLocaleString() : 'N/A'}
                            </p>
                            <p className="text-xs text-slate-400 mt-1">
                              by {entry.performed_by_name || entry.performed_by_email || 'System'}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-slate-900">Account Settings</h2>
              
              {/* Profile Card */}
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h3 className="text-lg font-medium text-slate-900 mb-4 flex items-center gap-2">
                  <User className="w-5 h-5 text-blue-600" />
                  Profile Information
                </h3>
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <label className="text-xs text-slate-500 uppercase">Name</label>
                    <p className="text-slate-900 font-medium">{user?.name}</p>
                  </div>
                  <div>
                    <label className="text-xs text-slate-500 uppercase">Email</label>
                    <p className="text-slate-900">{user?.email}</p>
                  </div>
                  <div>
                    <label className="text-xs text-slate-500 uppercase">Role</label>
                    <p className="text-slate-900">{user?.role || "Hospital User"}</p>
                  </div>
                  <div>
                    <label className="text-xs text-slate-500 uppercase">Health System</label>
                    <p className="text-slate-900">{healthSystem?.name}</p>
                  </div>
                </div>
              </div>

              {/* Security Card */}
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h3 className="text-lg font-medium text-slate-900 mb-4 flex items-center gap-2">
                  <Lock className="w-5 h-5 text-emerald-600" />
                  Security
                </h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                    <div>
                      <p className="font-medium text-slate-900">Password Login</p>
                      <p className="text-sm text-slate-500">
                        {hasPassword ? "You have a password set up" : "Set up a password to log in without access code"}
                      </p>
                    </div>
                    <Button 
                      onClick={() => setShowPasswordSetupModal(true)}
                      variant={hasPassword ? "outline" : "default"}
                      className={hasPassword ? "" : "bg-emerald-600 hover:bg-emerald-700"}
                    >
                      <Key className="w-4 h-4 mr-2" />
                      {hasPassword ? "Change Password" : "Set Password"}
                    </Button>
                  </div>
                  
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                    <div>
                      <p className="font-medium text-slate-900">Access Code</p>
                      <p className="text-sm text-slate-500">Your facility&apos;s access code for quick login</p>
                    </div>
                    <span className="text-sm text-slate-600 font-mono bg-slate-200 px-3 py-1 rounded">
                      {healthSystem?.access_code || "DEMO1234"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Notification Preferences - Granular Controls */}
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-slate-900 flex items-center gap-2">
                    <Bell className="w-5 h-5 text-amber-600" />
                    Notification Preferences
                  </h3>
                  <Button 
                    onClick={saveNotificationPrefs} 
                    disabled={notificationPrefsLoading}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {notificationPrefsLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Save className="w-4 h-4 mr-2" />
                    )}
                    Save Preferences
                  </Button>
                </div>
                
                <p className="text-sm text-slate-500 mb-4">
                  Control which notifications you receive via Email ({user?.email}) and Telegram.
                  {!telegramConnected && (
                    <span className="text-amber-600 ml-2">
                      ⚠️ Telegram not connected - contact admin to enable
                    </span>
                  )}
                </p>

                {/* Notification Table */}
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-700">Notification Type</th>
                        <th className="text-center py-3 px-4 text-sm font-medium text-slate-700">
                          <div className="flex items-center justify-center gap-2">
                            <Mail className="w-4 h-4 text-blue-600" />
                            Email
                          </div>
                        </th>
                        <th className="text-center py-3 px-4 text-sm font-medium text-slate-700">
                          <div className="flex items-center justify-center gap-2">
                            <Send className="w-4 h-4 text-sky-500" />
                            Telegram
                          </div>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {[
                        { key: "new_quickbid", label: "New QuickBid Response", desc: "When suppliers respond to your bids" },
                        { key: "compliance_alert", label: "Compliance Alert", desc: "License expirations, compliance issues" },
                        { key: "backorder_sync", label: "Backorder Status Change", desc: "When backorder status updates" },
                        { key: "workorder_new", label: "New Work Order Submitted", desc: "When a new work order is created" },
                        { key: "workorder_change", label: "Work Order Updated", desc: "When a work order is modified" },
                        { key: "workorder_complete", label: "Work Order Completed", desc: "When a work order is finished" },
                        { key: "workorder_cancel", label: "Work Order Cancelled", desc: "When a work order is cancelled" }
                      ].map((item) => (
                        <tr key={item.key} className="hover:bg-slate-50">
                          <td className="py-3 px-4">
                            <p className="font-medium text-slate-900">{item.label}</p>
                            <p className="text-xs text-slate-500">{item.desc}</p>
                          </td>
                          <td className="py-3 px-4 text-center">
                            <button
                              onClick={() => toggleNotificationPref(`email_${item.key}`)}
                              className={`w-12 h-6 rounded-full transition-colors relative ${
                                notificationPrefs[`email_${item.key}`] ? 'bg-blue-600' : 'bg-slate-300'
                              }`}
                              data-testid={`toggle-email-${item.key}`}
                            >
                              <span className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                                notificationPrefs[`email_${item.key}`] ? 'left-7' : 'left-1'
                              }`} />
                            </button>
                          </td>
                          <td className="py-3 px-4 text-center">
                            <button
                              onClick={() => toggleNotificationPref(`telegram_${item.key}`)}
                              disabled={!telegramConnected}
                              className={`w-12 h-6 rounded-full transition-colors relative ${
                                !telegramConnected ? 'bg-slate-200 cursor-not-allowed' :
                                notificationPrefs[`telegram_${item.key}`] ? 'bg-sky-500' : 'bg-slate-300'
                              }`}
                              data-testid={`toggle-telegram-${item.key}`}
                            >
                              <span className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                                notificationPrefs[`telegram_${item.key}`] ? 'left-7' : 'left-1'
                              }`} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Compliance Thresholds - Per Category */}
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-slate-900 flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-emerald-600" />
                    Compliance Thresholds
                  </h3>
                  <Button 
                    onClick={saveComplianceThresholds} 
                    disabled={thresholdsLoading}
                    className="bg-emerald-600 hover:bg-emerald-700"
                  >
                    {thresholdsLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Save className="w-4 h-4 mr-2" />
                    )}
                    Save Thresholds
                  </Button>
                </div>
                
                <p className="text-sm text-slate-500 mb-4">
                  Set custom thresholds for competitive bidding requirements. When a request exceeds the threshold, 
                  competitive bidding (QuickBid) will be automatically triggered.
                </p>

                {/* Enable/Disable Toggle */}
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg mb-4">
                  <div>
                    <p className="font-medium text-slate-900">Enable Competitive Bidding</p>
                    <p className="text-sm text-slate-500">Automatically require quotes for high-value requests</p>
                  </div>
                  <button
                    onClick={() => setComplianceThresholds(prev => ({ ...prev, enabled: !prev.enabled }))}
                    className={`w-14 h-7 rounded-full transition-colors relative ${
                      complianceThresholds.enabled ? 'bg-emerald-600' : 'bg-slate-300'
                    }`}
                    data-testid="toggle-competitive-bidding"
                  >
                    <span className={`absolute top-1 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                      complianceThresholds.enabled ? 'left-8' : 'left-1'
                    }`} />
                  </button>
                </div>

                {/* Category Thresholds */}
                {complianceThresholds.enabled && (
                  <div className="grid gap-4 md:grid-cols-2">
                    {[
                      { key: "suppliers_threshold", label: "Supplier Requests", icon: Package, desc: "Parts, supplies, materials" },
                      { key: "repairs_threshold", label: "Repair Services", icon: Wrench, desc: "Equipment repair, maintenance" },
                      { key: "equipment_threshold", label: "Equipment Purchases", icon: Settings, desc: "New equipment, devices" },
                      { key: "services_threshold", label: "Professional Services", icon: Briefcase, desc: "Consulting, installation" }
                    ].map((item) => {
                      const Icon = item.icon;
                      return (
                        <div key={item.key} className="p-4 border border-slate-200 rounded-lg">
                          <div className="flex items-center gap-2 mb-2">
                            <Icon className="w-4 h-4 text-slate-600" />
                            <span className="font-medium text-slate-900">{item.label}</span>
                          </div>
                          <p className="text-xs text-slate-500 mb-3">{item.desc}</p>
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                            <Input
                              type="number"
                              min="0"
                              step="100"
                              value={complianceThresholds[item.key]}
                              onChange={(e) => setComplianceThresholds(prev => ({
                                ...prev,
                                [item.key]: parseFloat(e.target.value) || 0
                              }))}
                              className="pl-8"
                              data-testid={`threshold-${item.key}`}
                            />
                          </div>
                          <p className="text-xs text-slate-400 mt-1">
                            Requests ≥ ${complianceThresholds[item.key]?.toLocaleString() || 0} require bidding
                          </p>
                        </div>
                      );
                    })}
                  </div>
                )}

                {!complianceThresholds.enabled && (
                  <div className="text-center py-6 bg-slate-50 rounded-lg">
                    <p className="text-slate-500">Competitive bidding is disabled. Enable to set thresholds.</p>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-slate-900">Team Members</h2>
                <p className="text-sm text-slate-500">
                  {hospitalUsers.length} user{hospitalUsers.length !== 1 ? 's' : ''} in your organization
                </p>
              </div>

              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Name</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Email</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Role</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {hospitalUsers.map((u) => (
                      <tr key={u.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                              <User className="w-4 h-4 text-blue-600" />
                            </div>
                            <span className="font-medium text-slate-900">{u.name}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-slate-600">{u.email}</td>
                        <td className="px-4 py-3">
                          <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs font-medium">
                            {u.role || "Staff"}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            u.registration_status === 'active' || u.is_active 
                              ? 'bg-emerald-100 text-emerald-700' 
                              : 'bg-amber-100 text-amber-700'
                          }`}>
                            {u.registration_status === 'active' || u.is_active ? 'Active' : 'Pending'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {hospitalUsers.length === 0 && (
                  <div className="py-12 text-center">
                    <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">No team members found</p>
                    <p className="text-xs text-slate-400 mt-1">Contact your administrator to add users</p>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics">
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-slate-900">Analytics Dashboard</h2>
              
              {/* Stats Cards */}
              <div className="grid md:grid-cols-4 gap-4">
                <div className="bg-white rounded-xl border border-slate-200 p-5">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                      <FileText className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-3xl font-bold text-slate-900">{analytics.total_requests}</p>
                      <p className="text-sm text-slate-500">Total Requests</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white rounded-xl border border-slate-200 p-5">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                      <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-3xl font-bold text-slate-900">{analytics.completed_requests}</p>
                      <p className="text-sm text-slate-500">Completed</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white rounded-xl border border-slate-200 p-5">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                      <Clock className="w-6 h-6 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-3xl font-bold text-slate-900">{analytics.pending_requests}</p>
                      <p className="text-sm text-slate-500">Pending</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white rounded-xl border border-slate-200 p-5">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                      <Activity className="w-6 h-6 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-3xl font-bold text-slate-900">{analytics.in_progress || 0}</p>
                      <p className="text-sm text-slate-500">In Progress</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Request Breakdown */}
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h3 className="text-lg font-medium text-slate-900 mb-4 flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-blue-600" />
                  Request Types
                </h3>
                <div className="space-y-3">
                  {REQUEST_TYPES.map(type => {
                    const count = requests.filter(r => r.request_type === type.id).length;
                    const percentage = analytics.total_requests > 0 ? (count / analytics.total_requests * 100).toFixed(0) : 0;
                    return (
                      <div key={type.id} className="flex items-center gap-3">
                        <span className="text-xl">{type.icon}</span>
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-medium text-slate-700">{type.label}</span>
                            <span className="text-sm text-slate-500">{count}</span>
                          </div>
                          <div className="w-full bg-slate-100 rounded-full h-2">
                            <div 
                              className="bg-blue-500 h-2 rounded-full transition-all"
                              style={{ width: `${percentage}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Backorder Summary */}
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h3 className="text-lg font-medium text-slate-900 mb-4 flex items-center gap-2">
                  <ShoppingCart className="w-5 h-5 text-emerald-600" />
                  Backorder Summary
                </h3>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="text-center p-4 bg-slate-50 rounded-lg">
                    <p className="text-2xl font-bold text-slate-900">{backorders.length}</p>
                    <p className="text-sm text-slate-500">Total Backorders</p>
                  </div>
                  <div className="text-center p-4 bg-slate-50 rounded-lg">
                    <p className="text-2xl font-bold text-emerald-600">
                      {backorders.filter(b => b.status === 'delivered').length}
                    </p>
                    <p className="text-sm text-slate-500">Delivered</p>
                  </div>
                  <div className="text-center p-4 bg-slate-50 rounded-lg">
                    <p className="text-2xl font-bold text-amber-600">
                      {backorders.filter(b => ['submitted', 'pending', 'quoted'].includes(b.status)).length}
                    </p>
                    <p className="text-sm text-slate-500">Pending</p>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>

      {/* New Request Modal — multi-item form mirroring backorder UX */}
      <NewMaintenanceRequestDialog
        open={showNewRequest}
        onOpenChange={setShowNewRequest}
        user={user}
        healthSystem={healthSystem}
        facilities={healthSystem?.facilities || []}
        onSubmitted={fetchData}
      />

      {/* Legacy single-issue dialog (kept hidden during multi-item rollout) */}
      <Dialog open={false} onOpenChange={setShowNewRequest}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wrench className="w-5 h-5 text-blue-600" />
              Report an Issue
            </DialogTitle>
            <DialogDescription>
              Tell us what&apos;s wrong and we&apos;ll dispatch help
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmitRequest} className="space-y-4">
            <div>
              <Label>Request Type *</Label>
              <Select 
                value={requestForm.request_type} 
                onValueChange={(v) => setRequestForm({...requestForm, request_type: v})}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select type..." />
                </SelectTrigger>
                <SelectContent>
                  {REQUEST_TYPES.map((type) => (
                    <SelectItem key={type.id} value={type.id}>
                      {type.icon} {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Facility</Label>
              <Select 
                value={requestForm.facility_id || user?.facility_id} 
                onValueChange={(v) => setRequestForm({...requestForm, facility_id: v})}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select facility..." />
                </SelectTrigger>
                <SelectContent>
                  {healthSystem?.facilities?.map((facility) => (
                    <SelectItem key={facility.id} value={facility.id}>
                      {facility.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Location Details</Label>
              <Input
                value={requestForm.location_details}
                onChange={(e) => setRequestForm({...requestForm, location_details: e.target.value})}
                placeholder="e.g., Building A, Floor 2, Room 204"
                className="mt-1"
              />
            </div>

            <div>
              <Label>Priority</Label>
              <Select 
                value={requestForm.priority} 
                onValueChange={(v) => setRequestForm({...requestForm, priority: v})}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="emergency">🔴 Emergency</SelectItem>
                  <SelectItem value="high">🟠 High</SelectItem>
                  <SelectItem value="normal">🔵 Normal</SelectItem>
                  <SelectItem value="low">⚪ Low</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Description *</Label>
              <Textarea
                value={requestForm.description}
                onChange={(e) => setRequestForm({...requestForm, description: e.target.value})}
                placeholder="Describe the issue in detail..."
                className="mt-1"
                rows={4}
              />
            </div>

            {/* Additional Email Recipients Section */}
            <div className="border-t border-slate-200 pt-4">
              <Label className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-slate-500" />
                Notify Additional People
              </Label>
              <p className="text-xs text-slate-500 mt-1 mb-2">
                Add colleagues who should be notified about this request
              </p>
              
              {/* Email input with add button */}
              <div className="flex gap-2">
                <Input
                  type="email"
                  value={newEmailInput}
                  onChange={(e) => setNewEmailInput(e.target.value)}
                  placeholder="colleague@hospital.org"
                  className="flex-1"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      if (newEmailInput && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmailInput)) {
                        if (!requestForm.additional_emails.includes(newEmailInput.toLowerCase())) {
                          setRequestForm({
                            ...requestForm,
                            additional_emails: [...requestForm.additional_emails, newEmailInput.toLowerCase()]
                          });
                        }
                        setNewEmailInput("");
                      }
                    }
                  }}
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    if (newEmailInput && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmailInput)) {
                      if (!requestForm.additional_emails.includes(newEmailInput.toLowerCase())) {
                        setRequestForm({
                          ...requestForm,
                          additional_emails: [...requestForm.additional_emails, newEmailInput.toLowerCase()]
                        });
                      }
                      setNewEmailInput("");
                    }
                  }}
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>

              {/* List of added emails */}
              {requestForm.additional_emails.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {requestForm.additional_emails.map((email, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 rounded-full text-sm"
                    >
                      {email}
                      <button
                        type="button"
                        onClick={() => {
                          setRequestForm({
                            ...requestForm,
                            additional_emails: requestForm.additional_emails.filter((_, i) => i !== index)
                          });
                        }}
                        className="hover:text-red-600"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Photo Upload Section */}
            <div className="border-t border-slate-200 pt-4">
              <Label className="flex items-center gap-2">
                <Camera className="w-4 h-4 text-slate-500" />
                Add Photos
              </Label>
              <p className="text-xs text-slate-500 mt-1 mb-2">
                Take a photo or upload images of the issue (optional)
              </p>
              
              {/* Hidden file inputs */}
              <input
                type="file"
                ref={photoInputRef}
                accept="image/*"
                className="hidden"
                onChange={(e) => handlePhotoUpload(e.target.files?.[0])}
              />
              <input
                type="file"
                ref={cameraInputRef}
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={(e) => handlePhotoUpload(e.target.files?.[0])}
              />
              
              {/* Upload buttons */}
              <div className="flex gap-2 mb-3">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => cameraInputRef.current?.click()}
                  disabled={uploadingPhoto}
                  className="flex-1"
                >
                  <Camera className="w-4 h-4 mr-2" />
                  Take Photo
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => photoInputRef.current?.click()}
                  disabled={uploadingPhoto}
                  className="flex-1"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Upload
                </Button>
              </div>
              
              {uploadingPhoto && (
                <div className="flex items-center gap-2 text-sm text-blue-600 mb-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Uploading photo...
                </div>
              )}
              
              {/* Photo previews */}
              {requestForm.photos.length > 0 && (
                <div className="grid grid-cols-3 gap-2">
                  {requestForm.photos.map((photo, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={photo}
                        alt={`Uploaded ${index + 1}`}
                        className="w-full h-20 object-cover rounded-lg border border-slate-200"
                      />
                      <button
                        type="button"
                        onClick={() => removePhoto(index)}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowNewRequest(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} className="bg-blue-600 hover:bg-blue-700">
                {submitting ? (
                  <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Submitting...</>
                ) : (
                  <><Send className="w-4 h-4 mr-2" /> Report Issue</>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Sign-Off Dialog */}
      <Dialog open={showSignOffDialog} onOpenChange={setShowSignOffDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardCheck className="w-5 h-5 text-blue-600" />
              Sign Off on Completed Work
            </DialogTitle>
            <DialogDescription>
              Review the completed work and provide your approval or feedback.
            </DialogDescription>
          </DialogHeader>

          {selectedRequest && (
            <div className="space-y-4">
              {/* Request Summary */}
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="font-medium text-slate-900">
                  {REQUEST_TYPES.find(t => t.id === selectedRequest.request_type)?.label || selectedRequest.request_type || "Maintenance Request"}
                </p>
                <p className="text-sm text-slate-600 mt-1">{selectedRequest.description}</p>
                <p className="text-xs text-slate-500 mt-2">
                  Location: {selectedRequest.location || selectedRequest.location_details || selectedRequest.facility_name}
                </p>
              </div>

              {/* Rating */}
              <div>
                <Label className="text-slate-700">Rate the Work Quality</Label>
                <div className="flex items-center gap-1 mt-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setSignOffData({...signOffData, rating: star})}
                      className="p-1 hover:scale-110 transition-transform"
                    >
                      <Star 
                        className={`w-8 h-8 ${
                          star <= signOffData.rating 
                            ? "text-amber-400 fill-amber-400" 
                            : "text-slate-200"
                        }`} 
                      />
                    </button>
                  ))}
                  <span className="ml-2 text-sm text-slate-600">{signOffData.rating}/5</span>
                </div>
              </div>

              {/* Feedback */}
              <div>
                <Label className="text-slate-700">Feedback (Optional)</Label>
                <Textarea
                  value={signOffData.feedback}
                  onChange={(e) => setSignOffData({...signOffData, feedback: e.target.value})}
                  placeholder="Any comments about the work performed..."
                  className="mt-1"
                  rows={3}
                />
              </div>
            </div>
          )}

          <DialogFooter className="flex gap-2 sm:gap-2">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => handleSignOff(false)}
              disabled={submitting}
              className="flex-1 border-red-200 text-red-600 hover:bg-red-50"
              data-testid="reject-work-btn"
            >
              <ThumbsDown className="w-4 h-4 mr-2" />
              Reject
            </Button>
            <Button 
              type="button"
              onClick={() => handleSignOff(true)}
              disabled={submitting}
              className="flex-1 bg-emerald-600 hover:bg-emerald-700"
              data-testid="approve-work-btn"
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <ThumbsUp className="w-4 h-4 mr-2" />
              )}
              Approve Work
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Request Modal - Enhanced with Contractor Selection */}
      <Dialog open={showEditRequestModal} onOpenChange={setShowEditRequestModal}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-blue-600" />
              Edit Work Order
            </DialogTitle>
            <DialogDescription>
              Update work order details including contractor assignment
            </DialogDescription>
          </DialogHeader>

          {editingRequest && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Request ID</Label>
                  <p className="text-sm font-mono text-slate-600 bg-slate-50 p-2 rounded">{editingRequest.id}</p>
                </div>
                <div>
                  <Label>Current Status</Label>
                  <p className="text-sm font-medium text-slate-600 bg-slate-50 p-2 rounded capitalize">
                    {editingRequest.status || editingRequest.stage || "pending"}
                  </p>
                </div>
              </div>
              
              <div>
                <Label>Location Details</Label>
                <Input
                  value={editingRequest.location_details || ""}
                  onChange={(e) => setEditingRequest({...editingRequest, location_details: e.target.value})}
                  placeholder="Building, floor, room..."
                />
              </div>
              
              <div>
                <Label>Description</Label>
                <Textarea
                  value={editingRequest.description || ""}
                  onChange={(e) => setEditingRequest({...editingRequest, description: e.target.value})}
                  rows={3}
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Priority</Label>
                  <Select 
                    value={editingRequest.priority || "normal"} 
                    onValueChange={(value) => setEditingRequest({...editingRequest, priority: value})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="normal">Normal</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="emergency">Emergency</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label className="flex items-center gap-2">
                    Assigned Contractor
                    {contractorsLoading && <Loader2 className="w-3 h-3 animate-spin" />}
                  </Label>
                  <Select 
                    value={editingRequest.assigned_contractor_id || "none"} 
                    onValueChange={(value) => setEditingRequest({
                      ...editingRequest, 
                      assigned_contractor_id: value === "none" ? null : value
                    })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select contractor..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">
                        <span className="text-slate-500">No contractor assigned</span>
                      </SelectItem>
                      {availableContractors.map((contractor) => (
                        <SelectItem key={contractor.id} value={contractor.id}>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{contractor.company_name || contractor.name}</span>
                            {contractor.rating && (
                              <span className="text-xs text-amber-600">★ {contractor.rating}</span>
                            )}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Contact Name</Label>
                  <Input
                    value={editingRequest.contact_name || ""}
                    onChange={(e) => setEditingRequest({...editingRequest, contact_name: e.target.value})}
                    placeholder="Contact person name"
                  />
                </div>
                <div>
                  <Label>Contact Phone</Label>
                  <Input
                    value={editingRequest.contact_phone || ""}
                    onChange={(e) => setEditingRequest({...editingRequest, contact_phone: e.target.value})}
                    placeholder="Phone number"
                  />
                </div>
              </div>
              
              <div>
                <Label>Internal Notes</Label>
                <Textarea
                  value={editingRequest.notes || ""}
                  onChange={(e) => setEditingRequest({...editingRequest, notes: e.target.value})}
                  rows={2}
                  placeholder="Additional notes visible to admin and contractor..."
                />
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700">
                <p className="flex items-center gap-2">
                  <Bell className="w-4 h-4" />
                  Changes will notify the Admin and assigned Contractor automatically.
                </p>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setShowEditRequestModal(false)}>Cancel</Button>
                <Button onClick={handleUpdateRequest} disabled={submitting} className="bg-blue-600 hover:bg-blue-700">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Changes
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal - Hard Delete with Audit */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Delete Work Order
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this work order?
            </DialogDescription>
          </DialogHeader>

          {deletingRequest && (
            <div className="space-y-4">
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="font-medium text-slate-900">{deletingRequest.id}</p>
                <p className="text-sm text-slate-600 mt-1">{deletingRequest.description?.substring(0, 100)}...</p>
                {deletingRequest.assigned_contractor_id && (
                  <p className="text-sm text-blue-600 mt-2">
                    ⚠️ Assigned contractor will be notified
                  </p>
                )}
              </div>
              
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm text-amber-800 font-medium flex items-center gap-2">
                  <History className="w-4 h-4" />
                  Audit Trail Preserved
                </p>
                <p className="text-xs text-amber-700 mt-1">
                  The record will be permanently deleted but archived for compliance. 
                  Admin and assigned contractors will be notified.
                </p>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>Keep Request</Button>
                <Button onClick={handleDeleteRequest} disabled={submitting} className="bg-red-600 hover:bg-red-700">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
                  Delete Permanently
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Request History Modal */}
      <Dialog open={showRequestHistoryModal} onOpenChange={setShowRequestHistoryModal}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wrench className="w-5 h-5 text-blue-600" />
              Request Details
            </DialogTitle>
            <DialogDescription>
              {viewingRequestHistory?.id} • {REQUEST_TYPES.find(t => t.id === viewingRequestHistory?.request_type)?.label || viewingRequestHistory?.request_type}
            </DialogDescription>
          </DialogHeader>

          {viewingRequestHistory && (
            <div className="space-y-4">
              {/* Request Summary */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-500 uppercase tracking-wide mb-2">Request Info</p>
                  <p className="font-medium text-slate-900">{viewingRequestHistory.description}</p>
                  <div className="flex gap-2 mt-2">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${getPriorityBadge(viewingRequestHistory.priority)}`}>
                      {viewingRequestHistory.priority?.toUpperCase()}
                    </span>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusBadge(viewingRequestHistory.status || viewingRequestHistory.stage)}`}>
                      {(viewingRequestHistory.status || viewingRequestHistory.stage)?.replace("_", " ").toUpperCase()}
                    </span>
                  </div>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-500 uppercase tracking-wide mb-2">Submitted By</p>
                  <p className="font-medium">{viewingRequestHistory.submitted_by || viewingRequestHistory.created_by_name}</p>
                  <p className="text-sm text-slate-500">{viewingRequestHistory.location_details || viewingRequestHistory.location}</p>
                  <p className="text-xs text-slate-400 mt-1">
                    {new Date(viewingRequestHistory.created_at).toLocaleString()}
                  </p>
                </div>
              </div>
              
              {/* Activity Timeline */}
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="text-xs text-slate-500 uppercase tracking-wide mb-3 flex items-center gap-2">
                  <History className="w-4 h-4" />
                  Activity Timeline
                </p>
                <div className="border-l-2 border-slate-200 pl-4 space-y-3 max-h-32 overflow-y-auto">
                  {viewingRequestHistory.audit_log?.map((entry, index) => (
                    <div key={index} className="relative">
                      <div className="absolute -left-[21px] w-3 h-3 bg-blue-500 rounded-full border-2 border-white" />
                      <div className="text-sm">
                        <p className="font-medium text-slate-900">{entry.action}</p>
                        <p className="text-slate-600 text-xs">{entry.by} • {new Date(entry.at).toLocaleString()}</p>
                      </div>
                    </div>
                  )) || (
                    <p className="text-sm text-slate-500">No history recorded yet</p>
                  )}
                </div>
              </div>

              {/* Ticket Chat */}
              <TicketChat
                ticketId={viewingRequestHistory.id}
                ticketType="maintenance"
                token={localStorage.getItem("hospital_token")}
                currentUser={{
                  id: user?.id,
                  name: user?.name,
                  email: user?.email,
                  role: "hospital"
                }}
              />

              <DialogFooter>
                <Button variant="outline" onClick={() => setShowRequestHistoryModal(false)}>Close</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Backorder Detail Modal - Unified View */}
      <BackorderDetailView
        open={showBackorderDetailModal}
        onClose={() => {
          setShowBackorderDetailModal(false);
          setSelectedBackorder(null);
        }}
        backorder={selectedBackorder}
        viewMode="hospital"
        token={localStorage.getItem("hospital_token")}
        onRefresh={fetchBackorders}
      />

      {/* Password Setup Modal */}
      <Dialog open={showPasswordSetupModal} onOpenChange={setShowPasswordSetupModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-emerald-600" />
              {hasPassword ? "Change Password" : "Set Up Password"}
            </DialogTitle>
            <DialogDescription>
              {hasPassword 
                ? "Enter a new password to update your login credentials" 
                : "Create a password so you can log in without an access code"}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>New Password</Label>
              <Input
                type="password"
                value={passwordForm.new_password}
                onChange={(e) => setPasswordForm({...passwordForm, new_password: e.target.value})}
                placeholder="Enter new password"
                className="mt-1"
              />
              <p className="text-xs text-slate-500 mt-1">Minimum 8 characters</p>
            </div>
            
            <div>
              <Label>Confirm Password</Label>
              <Input
                type="password"
                value={passwordForm.confirm_password}
                onChange={(e) => setPasswordForm({...passwordForm, confirm_password: e.target.value})}
                placeholder="Confirm new password"
                className="mt-1"
              />
            </div>

            {passwordForm.new_password && passwordForm.confirm_password && 
             passwordForm.new_password !== passwordForm.confirm_password && (
              <p className="text-sm text-red-600">Passwords do not match</p>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setShowPasswordSetupModal(false);
                setPasswordForm({ new_password: "", confirm_password: "" });
              }}>
                Cancel
              </Button>
              <Button 
                onClick={handleSetPassword} 
                disabled={settingPassword || !passwordForm.new_password || passwordForm.new_password !== passwordForm.confirm_password}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                {settingPassword ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Lock className="w-4 h-4 mr-2" />}
                {hasPassword ? "Update Password" : "Set Password"}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* New Backorder Modal */}
      <Dialog open={showNewBackorderModal} onOpenChange={setShowNewBackorderModal}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto p-0 bg-slate-50">
          <DialogHeader className="px-6 pt-6 pb-3 border-b border-slate-200 bg-white">
            <DialogTitle className="flex items-center gap-2 text-lg">
              <ShoppingCart className="w-5 h-5 text-emerald-600" />
              New Backorder Request
            </DialogTitle>
            <DialogDescription>
              Same form you would see on sterilemate.com — auto-tagged to your hospital so it appears in your dashboard.
            </DialogDescription>
          </DialogHeader>

          <div className="p-4 md:p-6">
            <QuickQuoteFormShared
              showWrapper={true}
              backLink={false}
              hospitalContext={{
                id: healthSystem?.id || user?.health_system_id,
                name: healthSystem?.name || user?.health_system_name,
                contact_name: user?.name,
                contact_email: user?.email,
                contact_phone: user?.phone,
              }}
              onSubmitted={(respData) => {
                const newId = respData?.backorder_id || respData?.request_id;
                if (newId) setHighlightedBackorderId(newId);
                setShowNewBackorderModal(false);
                toast.success("Backorder added to your dashboard");
                fetchData();
              }}
            />
          </div>
        </DialogContent>
      </Dialog>


      {/* New QuickBid Modal */}
      <Dialog open={showNewQuickBidModal} onOpenChange={setShowNewQuickBidModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Gavel className="w-5 h-5 text-amber-500" />
              Start New QuickBid
            </DialogTitle>
            <DialogDescription>
              Launch a competitive bidding session. Suppliers will be notified instantly.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Item Name *</Label>
              <Input
                placeholder="e.g., Autoclave Parts, Surgical Instruments"
                value={quickBidForm.item_name}
                onChange={(e) => setQuickBidForm({ ...quickBidForm, item_name: e.target.value })}
                data-testid="quickbid-item-name"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                placeholder="Detailed specifications, model numbers, etc."
                value={quickBidForm.description}
                onChange={(e) => setQuickBidForm({ ...quickBidForm, description: e.target.value })}
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Estimated Value ($) *</Label>
                <Input
                  type="number"
                  placeholder="2500"
                  min="2500"
                  value={quickBidForm.estimated_value}
                  onChange={(e) => setQuickBidForm({ ...quickBidForm, estimated_value: parseFloat(e.target.value) })}
                  data-testid="quickbid-estimated-value"
                />
                <p className="text-xs text-slate-500 mt-1">Min $2,500 for Texas compliance</p>
              </div>
              <div>
                <Label>Quantity</Label>
                <Input
                  type="number"
                  min="1"
                  value={quickBidForm.quantity}
                  onChange={(e) => setQuickBidForm({ ...quickBidForm, quantity: parseInt(e.target.value) })}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category</Label>
                <Select 
                  value={quickBidForm.category} 
                  onValueChange={(v) => setQuickBidForm({ ...quickBidForm, category: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="medical_equipment">Medical Equipment</SelectItem>
                    <SelectItem value="surgical_supplies">Surgical Supplies</SelectItem>
                    <SelectItem value="lab_equipment">Lab Equipment</SelectItem>
                    <SelectItem value="sterilization">Sterilization</SelectItem>
                    <SelectItem value="spare_parts">Spare Parts</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Bid Duration</Label>
                <Select 
                  value={String(quickBidForm.duration_hours)} 
                  onValueChange={(v) => setQuickBidForm({ ...quickBidForm, duration_hours: parseInt(v) })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 Hour (Flash)</SelectItem>
                    <SelectItem value="4">4 Hours</SelectItem>
                    <SelectItem value="24">24 Hours</SelectItem>
                    <SelectItem value="48">48 Hours</SelectItem>
                    <SelectItem value="72">72 Hours</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Urgency</Label>
              <Select 
                value={quickBidForm.urgency} 
                onValueChange={(v) => setQuickBidForm({ ...quickBidForm, urgency: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                  <SelectItem value="critical">Critical (Same Day)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewQuickBidModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateQuickBid}
              disabled={!quickBidForm.item_name || !quickBidForm.estimated_value || quickBidForm.estimated_value < 2500}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="submit-quickbid-btn"
            >
              <Gavel className="w-4 h-4 mr-2" />
              Launch QuickBid
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* QuickBid Detail Modal */}
      <Dialog open={showQuickBidDetailModal} onOpenChange={setShowQuickBidDetailModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-blue-500" />
              QuickBid Details
            </DialogTitle>
          </DialogHeader>
          {selectedQuickBid && (
            <div className="space-y-4 py-4">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-lg font-semibold">{selectedQuickBid.item_name}</h3>
                  <p className="text-sm text-slate-500">ID: {selectedQuickBid.id}</p>
                </div>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  selectedQuickBid.status === 'active' ? 'bg-amber-100 text-amber-700' :
                  selectedQuickBid.status === 'pending_award' ? 'bg-emerald-100 text-emerald-700' :
                  'bg-blue-100 text-blue-700'
                }`}>
                  {selectedQuickBid.status?.replace('_', ' ').toUpperCase()}
                </span>
              </div>
              
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="text-slate-600">{selectedQuickBid.description || "No description provided"}</p>
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-3 bg-slate-50 rounded-lg">
                  <p className="text-2xl font-bold text-slate-900">${selectedQuickBid.estimated_value?.toLocaleString()}</p>
                  <p className="text-xs text-slate-500">Estimated Value</p>
                </div>
                <div className="text-center p-3 bg-slate-50 rounded-lg">
                  <p className="text-2xl font-bold text-slate-900">{selectedQuickBid.bids_count || 0}</p>
                  <p className="text-xs text-slate-500">Bids Received</p>
                </div>
                <div className="text-center p-3 bg-slate-50 rounded-lg">
                  <p className="text-2xl font-bold text-emerald-600">${selectedQuickBid.lowest_bid?.toLocaleString() || "—"}</p>
                  <p className="text-xs text-slate-500">Best Bid</p>
                </div>
              </div>

              {/* Bids List */}
              {selectedQuickBid.bids && selectedQuickBid.bids.length > 0 && (
                <div className="border rounded-lg overflow-hidden">
                  <div className="px-4 py-3 bg-slate-50 border-b">
                    <h4 className="font-semibold">Supplier Bids</h4>
                  </div>
                  <div className="divide-y">
                    {selectedQuickBid.bids.map((bid, index) => (
                      <div key={bid.id || index} className="p-4 flex items-center justify-between">
                        <div>
                          <p className="font-medium">{bid.supplier_name || `Supplier ${index + 1}`}</p>
                          <p className="text-xs text-slate-500">Submitted: {new Date(bid.submitted_at).toLocaleString()}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold">${bid.amount?.toLocaleString()}</p>
                          {selectedQuickBid.status === 'pending_award' && (
                            <Button 
                              size="sm" 
                              className="mt-1 bg-emerald-600 hover:bg-emerald-700"
                              onClick={() => handleAwardQuickBid(bid.supplier_id, bid.amount)}
                            >
                              Award
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowQuickBidDetailModal(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit QuickBid Modal */}
      <Dialog open={showEditQuickBidModal} onOpenChange={setShowEditQuickBidModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="w-5 h-5 text-blue-500" />
              Edit QuickBid
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Item Name</Label>
              <Input
                value={quickBidForm.item_name}
                onChange={(e) => setQuickBidForm({ ...quickBidForm, item_name: e.target.value })}
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={quickBidForm.description}
                onChange={(e) => setQuickBidForm({ ...quickBidForm, description: e.target.value })}
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Estimated Value ($)</Label>
                <Input
                  type="number"
                  value={quickBidForm.estimated_value}
                  onChange={(e) => setQuickBidForm({ ...quickBidForm, estimated_value: parseFloat(e.target.value) })}
                />
              </div>
              <div>
                <Label>Quantity</Label>
                <Input
                  type="number"
                  value={quickBidForm.quantity}
                  onChange={(e) => setQuickBidForm({ ...quickBidForm, quantity: parseInt(e.target.value) })}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditQuickBidModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateQuickBid} className="bg-blue-600 hover:bg-blue-700">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Award Supplier Modal */}
      <Dialog open={showAwardModal} onOpenChange={setShowAwardModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trophy className="w-5 h-5 text-amber-500" />
              Award Supplier
            </DialogTitle>
            <DialogDescription>
              Select the winning bid and award the contract to a supplier.
            </DialogDescription>
          </DialogHeader>
          {selectedQuickBid && (
            <div className="space-y-4 py-4">
              <div className="bg-slate-50 rounded-lg p-4">
                <h4 className="font-medium">{selectedQuickBid.item_name}</h4>
                <p className="text-sm text-slate-500">Estimated: ${selectedQuickBid.estimated_value?.toLocaleString()}</p>
              </div>

              {selectedQuickBid.bids && selectedQuickBid.bids.length > 0 ? (
                <div className="space-y-2">
                  {selectedQuickBid.bids.map((bid, index) => (
                    <div key={bid.id || index} className="flex items-center justify-between p-4 border rounded-lg hover:bg-slate-50">
                      <div>
                        <p className="font-medium">{bid.supplier_name || `Supplier ${index + 1}`}</p>
                        <p className="text-lg font-bold text-emerald-600">${bid.amount?.toLocaleString()}</p>
                      </div>
                      <Button 
                        className="bg-emerald-600 hover:bg-emerald-700"
                        onClick={() => handleAwardQuickBid(bid.supplier_id, bid.amount)}
                      >
                        <Trophy className="w-4 h-4 mr-2" />
                        Award This Bid
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-slate-500">No bids received yet</p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAwardModal(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Backorder Edit Modal */}
      <Dialog open={showBackorderEditModal} onOpenChange={setShowBackorderEditModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="w-5 h-5 text-blue-500" />
              Edit Backorder
            </DialogTitle>
            <DialogDescription>
              Update your backorder details. Changes are tracked in the audit trail.
            </DialogDescription>
          </DialogHeader>
          {selectedBackorder && (
            <div className="space-y-4 py-4">
              {/* Item Info (Read Only) */}
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="font-medium text-slate-900">{selectedBackorder.items?.[0]?.name || selectedBackorder.item_name || "Item"}</p>
                <p className="text-xs text-slate-500">ID: {selectedBackorder.id}</p>
              </div>

              {/* Priority */}
              <div>
                <Label>Internal Priority *</Label>
                <Select 
                  value={backorderEditForm.internal_priority} 
                  onValueChange={(v) => setBackorderEditForm({ ...backorderEditForm, internal_priority: v })}
                >
                  <SelectTrigger data-testid="backorder-priority-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="critical">🔴 Critical - Immediate Need</SelectItem>
                    <SelectItem value="high">🟠 High - Within 24 Hours</SelectItem>
                    <SelectItem value="medium">🟡 Medium - Within 3 Days</SelectItem>
                    <SelectItem value="low">🟢 Low - Standard</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Required Date */}
              <div>
                <Label>Required By Date</Label>
                <Input
                  type="date"
                  value={backorderEditForm.required_date}
                  onChange={(e) => setBackorderEditForm({ ...backorderEditForm, required_date: e.target.value })}
                  data-testid="backorder-required-date"
                />
              </div>

              {/* Department Notes */}
              <div>
                <Label>Department Notes</Label>
                <Textarea
                  placeholder="e.g., Critical for Surgery Dept, SPD Low Stock..."
                  value={backorderEditForm.department_notes}
                  onChange={(e) => setBackorderEditForm({ ...backorderEditForm, department_notes: e.target.value })}
                  rows={3}
                  data-testid="backorder-dept-notes"
                />
                <p className="text-xs text-slate-500 mt-1">Notes visible to your team and admins</p>
              </div>

              {/* Department */}
              <div>
                <Label>Requesting Department</Label>
                <Input
                  placeholder="e.g., Surgery, SPD, Lab..."
                  value={backorderEditForm.department}
                  onChange={(e) => setBackorderEditForm({ ...backorderEditForm, department: e.target.value })}
                />
              </div>

              {/* Contact Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Contact Name</Label>
                  <Input
                    placeholder="Contact person"
                    value={backorderEditForm.contact_name}
                    onChange={(e) => setBackorderEditForm({ ...backorderEditForm, contact_name: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Contact Phone</Label>
                  <Input
                    placeholder="Phone number"
                    value={backorderEditForm.contact_phone}
                    onChange={(e) => setBackorderEditForm({ ...backorderEditForm, contact_phone: e.target.value })}
                  />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBackorderEditModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateBackorder} className="bg-blue-600 hover:bg-blue-700" data-testid="save-backorder-btn">
              <Save className="w-4 h-4 mr-2" />
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Audit Trail Modal */}
      <Dialog open={showAuditTrailModal} onOpenChange={setShowAuditTrailModal}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-slate-500" />
              Audit Trail
            </DialogTitle>
            <DialogDescription>
              Complete history of changes for this backorder
            </DialogDescription>
          </DialogHeader>
          {selectedBackorder && (
            <div className="space-y-4 py-4">
              {/* Backorder Summary */}
              <div className="bg-slate-50 rounded-lg p-4 flex items-center justify-between">
                <div>
                  <p className="font-medium text-slate-900">{selectedBackorder.items?.[0]?.name || selectedBackorder.item_name || "Item"}</p>
                  <p className="text-xs text-slate-500">ID: {selectedBackorder.id}</p>
                </div>
                <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusBadge(selectedBackorder.status)}`}>
                  {selectedBackorder.status?.replace("_", " ").toUpperCase() || "PENDING"}
                </span>
              </div>

              {/* Audit Entries */}
              <div className="max-h-[400px] overflow-y-auto">
                {auditTrailLoading ? (
                  <div className="py-8 text-center">
                    <RefreshCw className="w-6 h-6 text-slate-400 mx-auto animate-spin" />
                    <p className="text-sm text-slate-500 mt-2">Loading audit trail...</p>
                  </div>
                ) : auditTrailData.length > 0 ? (
                  <div className="space-y-3">
                    {auditTrailData.map((entry, index) => (
                      <div key={index} className="border border-slate-200 rounded-lg p-4">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-2">
                            {entry.type === 'field_change' ? (
                              <span className="w-6 h-6 bg-blue-100 rounded flex items-center justify-center">
                                <Pencil className="w-3 h-3 text-blue-600" />
                              </span>
                            ) : (
                              <span className="w-6 h-6 bg-emerald-100 rounded flex items-center justify-center">
                                <Activity className="w-3 h-3 text-emerald-600" />
                              </span>
                            )}
                            <span className="font-medium text-slate-900 capitalize">
                              {entry.type === 'field_change' ? entry.field?.replace(/_/g, ' ') : entry.action?.replace(/_/g, ' ')}
                            </span>
                          </div>
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            entry.changed_by_type === 'admin' ? 'bg-purple-100 text-purple-700' :
                            entry.changed_by_type === 'hospital' ? 'bg-blue-100 text-blue-700' :
                            'bg-slate-100 text-slate-700'
                          }`}>
                            {entry.changed_by_type || 'system'}
                          </span>
                        </div>
                        
                        {entry.type === 'field_change' ? (
                          <div className="text-sm">
                            <span className="text-slate-500">Changed from </span>
                            <span className="text-red-600 line-through">{entry.old_value || '(empty)'}</span>
                            <span className="text-slate-500"> to </span>
                            <span className="text-emerald-600 font-medium">{entry.new_value}</span>
                          </div>
                        ) : (
                          <p className="text-sm text-slate-600">{entry.details}</p>
                        )}
                        
                        <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-100 text-xs text-slate-500">
                          <span>By: {entry.changed_by || entry.changed_by_name || 'System'}</span>
                          <span>{entry.timestamp ? new Date(entry.timestamp).toLocaleString() : '—'}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="py-8 text-center">
                    <History className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">No audit entries yet</p>
                    <p className="text-xs text-slate-400">Changes will be tracked here</p>
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAuditTrailModal(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Floating Report Issue Button */}
      <button
        onClick={() => setShowNewRequest(true)}
        className="fixed bottom-6 right-6 z-50 flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-full shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-200 group"
        data-testid="floating-report-issue-btn"
      >
        <Wrench className="w-5 h-5 group-hover:rotate-12 transition-transform" />
        <span className="font-semibold">Report Issue</span>
      </button>
    </HospitalLayout>
  );
}
